import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import $ from 'jquery';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { Tooltip } from 'bootstrap';
import Dropdown from 'bootstrap/js/dist/dropdown';
import {
  createWorkOrder,
  createWorkOrderAndEvent,
  updateWorkOrder,
  deleteWorkOrder,
  findWorkOrderByNumber,
  getPaginatedWorkOrders,
  getWorkOrderById,
  createWorkOrderWithAi,
} from '../api/work-orderAPI';
import { useSetting } from '../utils/AppSettingsContext';
import { useFormBuilder } from '../utils/useFormBuilder';
import { useTableFilters } from '../utils/useTableFilters';
import { getCache } from '../utils/lookupCache';
import { validateForm, useValidator } from '../utils/useValidator';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import { toUtcIsoWithTime } from '../utils/dateUtils';
import {
  buildWorkOrderCompleteFields,
  buildWorkOrderStatusFields,
  buildWorkOrderValidationFields,
  getWorkOrderStatusValidationErrors,
} from '../utils/workOrderFormHelpers';

import { fetchData } from '../utils/apiHelpers';
import { create, update, deleteOne, getAll } from '../api';
import { getWorkOrderPartsByWorkOrderId } from '../api/work-order-partsAPI';
import { getBillOfEquipmentWosByWorkOrderId } from '../api/bill-of-equipment-woAPI';
import { getBillOfLabourWosByWorkOrderId } from '../api/bill-of-labour-woAPI';
import { getBillOfServicesWosByWorkOrderId } from '../api/bill-of-services-woAPI';
import { getWorkOrderFormsByWorkOrderId, createWorkOrderForm, deleteWorkOrderForm } from '../api/work-order-formsAPI';
import { getFormAssignments } from '../api/form-assignmentsAPI';
import { getForms, getFormById } from '../api/formsAPI';
import { createFormData } from '../api/form-dataAPI';
import {
  createWorkOrderFormAction,
  getWorkOrderFormActionsByWorkOrderFormId,
  deleteWorkOrderFormAction,
} from '../api/work-order-form-actionsAPI';
import { getAssetByAssetNumber, getAssetById, getPaginatedAssetsAndAreas } from '../api/assetAPI';
import { getAreaById } from '../api/areaAPI';
import { getPurchaseOrderLineById } from '../api/purchase-order-linesAPI';
import { getPurchaseOrderById } from '../api/purchase-orderAPI';
import { getTrades } from '../api/tradesAPI';
import { getTechnicians } from '../api/techniciansAPI'
import { getExpenseCodes } from '../api/expense-codeAPI';
import { getExternalServices } from '../api/external-servicesAPI';
import { createCostItem, getCostItemsByWorkOrderId, updateCostItem } from '../api/cost-itemsAPI';
import { getTeamsById } from '../api/teamsAPI';
import { getWorkOrderTasksByAsset } from '../api/tasksAPI';
import { getBillOfMaterialsByTaskId } from '../api/bill-of-materialsAPI';
import { getBillOfLabourByTaskId } from '../api/bill-of-labourAPI';
import { getBillOfServicesByTaskId } from '../api/bill-of-servicesAPI';
import { getBillOfEquipmentByTaskId } from '../api/bill-of-equipmentAPI';
import WorkOrderCalendarLayer from './WorkOrderCalendarLayer';
import WorkOrderViewModal from './WorkOrderViewModal';
import { saveWorkOrderParts } from './WorkOrderPartsTab';
import WorkOrderPanelView from './WorkOrderPanelView';
import { useWorkOrderSchedule } from '../hook/useWorkOrderSchedule';
import { useWorkOrderScheduleFields } from '../hook/useWorkOrderScheduleFields';
import FormViewModal from './FormViewModal';
import WorkOrderDataTable from './WorkOrderDataTable';
import WorkOrderEditModal, { getInitialWorkOrder, mapTaskBillingToWorkOrderItems } from './WorkOrderEditModal';
import { deleteUploadedFile } from '../api/uploadAPI';
import {
  handleExportExcel as exportWorkOrdersExcel,
  handleExportCSV as exportWorkOrdersCSV,
  handleExportPdf as exportWorkOrderPdf,
} from './WorkOrderExports';

const PANEL_ASSIGNMENT_DEFAULT = 'unassigned';
const PANEL_SORT_DEFAULT = 'date-desc';
const PANEL_SORT_MAPPINGS = {
  'date-asc': { field: 'createdAt', order: 'asc' },
  'date-desc': { field: 'createdAt', order: 'desc' },
  'number-asc': { field: 'number', order: 'asc' },
  'priority-high': { field: 'priorityLevel', order: 'asc' },
  'priority-low': { field: 'priorityLevel', order: 'desc' },
};

const normalizeFileKey = (value) => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  try {
    const url = new URL(trimmed);
    return url.pathname.replace(/^\//, '');
  } catch (err) {
    return trimmed.replace(/^\/+/, '');
  }
};

const extractDeletionTargets = (fileEntry, fallback) => {
  const candidates = [];
  if (fileEntry) {
    if (typeof fileEntry === 'string') {
      candidates.push(fileEntry);
    } else if (typeof fileEntry === 'object') {
      const { key, filename, name, url } = fileEntry;
      if (typeof key === 'string') candidates.push(key);
      if (typeof filename === 'string') candidates.push(filename);
      if (typeof name === 'string') candidates.push(name);
      if (typeof url === 'string') candidates.push(url);
    }
  }
  if (typeof fallback === 'string') {
    candidates.push(fallback);
  }

  const firstValid = candidates.find((item) => typeof item === 'string' && item.trim());
  const normalizedKey = normalizeFileKey(firstValid);
  const url =
    fileEntry && typeof fileEntry === 'object' && typeof fileEntry.url === 'string'
      ? fileEntry.url.trim()
      : undefined;

  return { key: normalizedKey, url };
};

const removeMatchingUrls = (urls, key, url) => {
  if (!Array.isArray(urls)) return urls;
  if (!key && !url) return urls;
  let modified = false;
  const filtered = urls.filter((item) => {
    if (typeof item !== 'string') return true;
    const trimmed = item.trim();
    if (url && trimmed === url) {
      modified = true;
      return false;
    }
    if (key && normalizeFileKey(trimmed) === key) {
      modified = true;
      return false;
    }
    return true;
  });

  return modified ? filtered : urls;
};

const parseJsonArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
      return [trimmed];
    }
  }
  return [];
};

const normalizeImageArray = (value) => (
  parseJsonArray(value)
    .map((entry) => {
      if (typeof entry === 'string') return entry.trim();
      if (!entry || typeof entry !== 'object') return '';
      if (typeof entry.key === 'string' && entry.key.trim()) return entry.key.trim();
      if (typeof entry.filename === 'string' && entry.filename.trim()) return entry.filename.trim();
      if (typeof entry.name === 'string' && entry.name.trim()) return entry.name.trim();
      if (typeof entry.url === 'string' && entry.url.trim()) return normalizeFileKey(entry.url);
      return '';
    })
    .filter(Boolean)
);

const normalizeIdArray = (value) => {
  const ids = parseJsonArray(value)
    .map((item) => Number(item))
    .filter((id) => Number.isFinite(id));
  return [...new Set(ids)];
};

const WorkOrderListLayer = ({ viewMode }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [headerSearchContainer, setHeaderSearchContainer] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [newWorkOrder, setNewWorkOrder] = useState({});
  const [showAiModal, setShowAiModal] = useState(false);
  const [aiPromptText, setAiPromptText] = useState('');
  const [aiError, setAiError] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [selectedAssign, setSelectedAssign] = useState(null);
  const [assignType, setAssignType] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [crew, setCrew] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [teamTechnicianIds, setTeamTechnicianIds] = useState(null);
  const [isAdvancedAsset, setIsAdvancedAsset] = useState(false);
  const [workOrderParts, setWorkOrderParts] = useState([]);
  const [deletedPartIds, setDeletedPartIds] = useState([]);
  const [workOrderEquipment, setWorkOrderEquipment] = useState([]);
  const [deletedEquipmentIds, setDeletedEquipmentIds] = useState([]);
  const [workOrderLabour, setWorkOrderLabour] = useState([]);
  const [deletedLabourIds, setDeletedLabourIds] = useState([]);
  const [workOrderServices, setWorkOrderServices] = useState([]);
  const [deletedServiceIds, setDeletedServiceIds] = useState([]);
  const [workOrderCostItems, setWorkOrderCostItems] = useState([]);
  const [actualPartsCost, setActualPartsCost] = useState('');
  const [actualLabourCost, setActualLabourCost] = useState('');
  const [actualMiscCost, setActualMiscCost] = useState('');
  const [actualLabourHours, setActualLabourHours] = useState('');
  const [assetTaskOptions, setAssetTaskOptions] = useState([]);
  const [assetTasksById, setAssetTasksById] = useState({});
  const [selectedTaskOption, setSelectedTaskOption] = useState(null);
  const [workOrderForms, setWorkOrderForms] = useState([]);
  const [showFormModal, setShowFormModal] = useState(false);
  const [formOptions, setFormOptions] = useState([]);
  const [selectedFormId, setSelectedFormId] = useState('');
  const [formTargetWorkOrder, setFormTargetWorkOrder] = useState(null);
  const [showFormView, setShowFormView] = useState(false);
  const [viewWorkOrderFormId, setViewWorkOrderFormId] = useState(null);
  const [showEquipmentModal, setShowEquipmentModal] = useState(false);
  const [editingEquipmentIndex, setEditingEquipmentIndex] = useState(null);
  const [equipmentForm, setEquipmentForm] = useState({});
  const [equipmentErrors, setEquipmentErrors] = useState({});
  const [showLabourModal, setShowLabourModal] = useState(false);
  const [editingLabourIndex, setEditingLabourIndex] = useState(null);
  const [labourForm, setLabourForm] = useState({});
  const isSavingRef = useRef(false);
  const [tradeOptions, setTradeOptions] = useState([]);
  const [expenseOptions, setExpenseOptions] = useState([]);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [imageModalSrc, setImageModalSrc] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedAssetDetails, setSelectedAssetDetails] = useState(null);
  const taskSelectionInitializedRef = useRef(false);
  const prevTaskIdRef = useRef(null);
  const workOrderPartsRef = useRef(workOrderParts);
  const workOrderLabourRef = useRef(workOrderLabour);
  const workOrderServicesRef = useRef(workOrderServices);
  const workOrderEquipmentRef = useRef(workOrderEquipment);
  const normalizeDurationValue = useCallback((value) => {
    if (value === '' || value === null || value === undefined) return value;
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return value;
    const [, decimals = ''] = String(value).split('.');
    if (decimals.length <= 2) return String(value);
    const truncated = Math.trunc(numeric * 100) / 100;
    return truncated.toFixed(2);
  }, []);
  const handleDurationChange = (value) => {
    durationEditedRef.current = true;
    setDuration(value);
  };
  const handleActualDurationChange = (value) => {
    actualDurationEditedRef.current = true;
    setActualDuration(value);
  };
  const handleCreateEventToggle = (checked) => {
    setNewWorkOrder((prev) => ({
      ...prev,
      createEvent: checked,
    }));
  };
  const getCostItemFieldValue = (item, fields) => {
    if (!item) return null;
    for (const field of fields) {
      if (item[field] !== undefined && item[field] !== null) {
        return item[field];
      }
    }
    return null;
  };
  const formatCostInputValue = (value) => {
    if (value === null || value === undefined) return '';
    const number = Number(value);
    if (!Number.isFinite(number)) return '';
    return String(value);
  };
  const parseCostInputValue = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
  };
  const primaryCostItem = Array.isArray(workOrderCostItems) && workOrderCostItems.length > 0
    ? workOrderCostItems[0]
    : null;
  const isWorkOrderLocked = useMemo(
    () => [4, 5].includes(Number(newWorkOrder?.statusId)),
    [newWorkOrder?.statusId],
  );
  const actualCostTotals = useMemo(() => ({
    parts: parseCostInputValue(getCostItemFieldValue(primaryCostItem, ['partsActualCost', 'parts_actual_cost'])),
    labour: parseCostInputValue(getCostItemFieldValue(primaryCostItem, ['labourActualCost', 'labour_actual_cost'])),
    misc: parseCostInputValue(getCostItemFieldValue(primaryCostItem, ['miscActualCost', 'misc_actual_cost'])),
  }), [primaryCostItem]);
  const createEventValidationFields = useMemo(
    () =>
      newWorkOrder.createEvent
        ? [
          { field: 'scheduledAt', label: 'Scheduled Start', type: 'datetime', required: true },
          { field: 'dueDate', label: 'Scheduled End', type: 'datetime', required: true },
        ]
        : [],
    [newWorkOrder.createEvent],
  );

  useEffect(() => {
    setHeaderSearchContainer(document.getElementById('header-search-container'));
  }, []);
  const {
    errors: createEventErrors,
    setErrors: setCreateEventErrors,
    validate: validateCreateEvent,
  } = useValidator(createEventValidationFields, newWorkOrder);
  const [, setFormReadOnly] = useState(false);
  const [showView, setShowView] = useState(false);
  const userProfile = useUserProfile();
  const canUpdateWorkOrder =
    userProfile?.privileges?.includes('work-orders:update');
  const canCreateWorkOrder = userProfile?.privileges?.includes('work-orders:create');
  const canCreateWorkOrderEvent = userProfile?.privileges?.includes('events:create') && userProfile?.privileges?.includes('work-orders:create');
  useEffect(() => {
    workOrderPartsRef.current = workOrderParts;
  }, [workOrderParts]);

  useEffect(() => {
    workOrderLabourRef.current = workOrderLabour;
  }, [workOrderLabour]);

  useEffect(() => {
    workOrderServicesRef.current = workOrderServices;
  }, [workOrderServices]);

  useEffect(() => {
    workOrderEquipmentRef.current = workOrderEquipment;
  }, [workOrderEquipment]);

  useEffect(() => {
    if (!showModal || editingId !== prevTaskIdRef.current) {
      taskSelectionInitializedRef.current = false;
      prevTaskIdRef.current = null;
    }
  }, [showModal, editingId]);

  const showCreateEventToggle = !editingId && isAdvancedAsset // && canCreateWorkOrderEvent;
  const { getSchedule, updateSchedule, deleteSchedule } = useWorkOrderSchedule();
  const [workOrderSchedule, setWorkOrderSchedule] = useState(null);
  const [scheduleErrors, setScheduleErrors] = useState({});
  const originalScheduleRef = useRef(null);
  const scheduleChangedRef = useRef(false);
  const [showScheduleWarning, setShowScheduleWarning] = useState(false);

  useEffect(() => {
    if (!newWorkOrder.createEvent) {
      setCreateEventErrors({});
    }
  }, [newWorkOrder.createEvent, setCreateEventErrors]);

  useEffect(() => {
    if (!isAdvancedAsset && newWorkOrder.createEvent) {
      setNewWorkOrder((prev) => ({ ...prev, createEvent: false }));
    }
  }, [isAdvancedAsset, newWorkOrder.createEvent]);

  const handleImageClick = useCallback((src) => {
    setImageModalSrc(src);
    setShowImageModal(true);
  }, []);

  const scheduleFields = useWorkOrderScheduleFields({
    workOrderSchedule: workOrderSchedule || {},
    setWorkOrderSchedule,
    setScheduleErrors,
  });
  const { renderField: renderScheduleField } = useFormBuilder(
    scheduleFields,
    workOrderSchedule || {},
    setWorkOrderSchedule,
    scheduleErrors,
  );

  // Loading indicator for modal data fetches
  const [modalLoading, setModalLoading] = useState(false);
  const listContainerRef = useRef(null);
  const [workOrders, setWorkOrders] = useState([]);
  const [woPage, setWoPage] = useState(1);
  const [woHasMore, setWoHasMore] = useState(true);
  const [woLoading, setWoLoading] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [calendarRefresh, setCalendarRefresh] = useState(0);
  const [panelAssignmentFilter, setPanelAssignmentFilter] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('completed') === 'true' ? 'complete' : PANEL_ASSIGNMENT_DEFAULT;
  });
  const [panelSortOption, setPanelSortOption] = useState(PANEL_SORT_DEFAULT);
  const panelSortSelection = useMemo(
    () => PANEL_SORT_MAPPINGS[panelSortOption] || PANEL_SORT_MAPPINGS[PANEL_SORT_DEFAULT],
    [panelSortOption],
  );
  const panelSortField = panelSortSelection?.field ?? null;
  const panelSortOrder = panelSortSelection?.order ?? null;
  const normalizedSearchTerm = useMemo(() => searchTerm.trim().toLowerCase(), [searchTerm]);
  const filteredWorkOrders = useMemo(() => {
    if (!normalizedSearchTerm) {
      return workOrders;
    }

    return workOrders.filter((workOrder) => {
      const assetNumber =
        workOrder.asset?.assetNumber ||
        workOrder.asset?.unitNumber ||
        workOrder.assetNumber ||
        '';
      const assetDescription =
        workOrder.asset?.assetDescription ||
        workOrder.assetDescription ||
        '';
      const workOrderNumber = workOrder.workOrderNumber || '';
      const workOrderTitle = workOrder.workOrderTitle || '';
      const searchTargets = [workOrderNumber, workOrderTitle, assetNumber, assetDescription];

      return searchTargets.some((value) =>
        String(value).toLowerCase().includes(normalizedSearchTerm),
      );
    });
  }, [normalizedSearchTerm, workOrders]);
  const docTableRef = useRef(null);
  const docDtRef = useRef(null);
  const [duration, setDuration] = useState('');
  const [actualDuration, setActualDuration] = useState('');
  const durationEditedRef = useRef(false);
  const actualDurationEditedRef = useRef(false);
  const prevScheduledAtRef = useRef(null);
  const prevStartedAtRef = useRef(null);
  const scheduledDurationRef = useRef(null);
  const prevStatusIdRef = useRef(null);
  const originalWorkOrderRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const workOrderNumberParam = searchParams.get('workordernumber');
  const startDateParam = searchParams.get('startDate');
  const endDateParam = searchParams.get('endDate');
  const dateFilterTypeParam = searchParams.get('dateFilterType');
  const dataFilterTypeParam = searchParams.get('dataFilterType');
  const dataFromElementParam = searchParams.get('dataFromElement');
  const completedParam = searchParams.get('completed');
  const taskTypeParam = searchParams.get('taskType');
  const clearFilterQueryParams = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);
  const complianceParam = searchParams.get('compliance');
  const departmentIdsParam = searchParams.get('departmentIds');
  const [initialWorkOrderParam] = useState(workOrderNumberParam);
  const [initialCompletedParam] = useState(completedParam);
  const [initialTaskTypeParam] = useState(taskTypeParam);
  const [initialStartDateParam] = useState(startDateParam);
  const [initialEndDateParam] = useState(endDateParam);
  const [initialComplianceParam] = useState(complianceParam);
  const [initialDepartmentIdsParam] = useState(departmentIdsParam);

  const getDateString = (date) => date.toISOString().split('T')[0];
  const todayDate = new Date();
  const sevenDaysLater = new Date(todayDate);
  sevenDaysLater.setDate(todayDate.getDate() + 7);
  const [customScheduledStart, setCustomScheduledStart] = useState(getDateString(todayDate));
  const [customScheduledEnd, setCustomScheduledEnd] = useState(getDateString(sevenDaysLater));
  // Refs keep the renderAfter callback stable (no tableFilters churn on date change)
  const customScheduledStartRef = useRef(customScheduledStart);
  const customScheduledEndRef = useRef(customScheduledEnd);
  customScheduledStartRef.current = customScheduledStart;
  customScheduledEndRef.current = customScheduledEnd;
  const [customActualStart, setCustomActualStart] = useState(initialStartDateParam || getDateString(todayDate));
  const [customActualEnd, setCustomActualEnd] = useState(initialEndDateParam || getDateString(todayDate));
  const customActualStartRef = useRef(customActualStart);
  const customActualEndRef = useRef(customActualEnd);
  customActualStartRef.current = customActualStart;
  customActualEndRef.current = customActualEnd;
  const renderScheduledWithinAfter = useCallback((currentTempFilters) => {
    if (currentTempFilters.scheduledWithinDays !== "custom") return null;
    return (
      <div className="col-12 mt-2">
        <div className="row">
          <div className="col-md-6 d-flex flex-column">
            <label className="form-label">Scheduled Start Date</label>
            <input
              type="date"
              className="form-control"
              value={customScheduledStartRef.current}
              onChange={(e) => setCustomScheduledStart(e.target.value)}
            />
          </div>
          <div className="col-md-6 d-flex flex-column">
            <label className="form-label">Scheduled End Date</label>
            <input
              type="date"
              className="form-control"
              value={customScheduledEndRef.current}
              onChange={(e) => setCustomScheduledEnd(e.target.value)}
            />
          </div>
        </div>
      </div>
    );
  }, []);
  const renderActualWithinAfter = useCallback((currentTempFilters) => {
    if (currentTempFilters.actualWithinDays !== "custom") return null;
    return (
      <div className="col-12 mt-2">
        <div className="row">
          <div className="col-md-6 d-flex flex-column">
            <label className="form-label">Actual Start Date</label>
            <input
              type="date"
              className="form-control"
              value={customActualStartRef.current}
              onChange={(e) => setCustomActualStart(e.target.value)}
            />
          </div>
          <div className="col-md-6 d-flex flex-column">
            <label className="form-label">Actual End Date</label>
            <input
              type="date"
              className="form-control"
              value={customActualEndRef.current}
              onChange={(e) => setCustomActualEnd(e.target.value)}
            />
          </div>
        </div>
      </div>
    );
  }, []);

  // Get work order settings
  const autoGenWorkOrderNo = useSetting('auto_generate_work_order');
  const defaultPartsExpenseCode = useSetting('default_expense_parts');
  const defaultLabourExpenseCode = useSetting('default_expense_labour');
  const defaultServiceExpenseCode = useSetting('default_expense_services');
  const useCostResponsibility = useSetting('use_cost_responsibility');
  let disabled = '';

  if (autoGenWorkOrderNo) { disabled = "disabled" };

  const normalizeDateValue = (value) => {
    if (value === undefined || value === null) {
      return null;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed ? trimmed : null;
    }
    return value;
  };

  const toNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const parsed = typeof value === 'string' ? parseFloat(value) : Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const partsCostTotal = useMemo(() => {
    if (!Array.isArray(workOrderParts)) return 0;
    return workOrderParts.reduce((sum, part) => {
      const quantity = toNumber(part.quantity ?? part.qty);
      return sum + toNumber(part.cost) * quantity;
    }, 0);
  }, [workOrderParts]);

  const labourCostTotal = useMemo(() => {
    if (!Array.isArray(workOrderLabour)) return 0;
    return workOrderLabour.reduce((sum, labour) => {
      const quantity = toNumber(labour.qty ?? labour.quantity);
      return sum + quantity * toNumber(labour.labourHours) * toNumber(labour.labourRate);
    }, 0);
  }, [workOrderLabour]);

  const labourHoursTotal = useMemo(() => {
    if (!Array.isArray(workOrderLabour)) return 0;
    return workOrderLabour.reduce((sum, labour) => {
      const quantity = toNumber(labour.qty ?? labour.quantity);
      return sum + quantity * toNumber(labour.labourHours);
    }, 0);
  }, [workOrderLabour]);

  const servicesCostTotal = useMemo(() => {
    if (!Array.isArray(workOrderServices)) return 0;
    return workOrderServices.reduce((sum, service) => {
      const quantity = toNumber(service.quantity ?? service.qty);
      return sum + quantity * toNumber(service.cost);
    }, 0);
  }, [workOrderServices]);

  const deepEqual = (a, b) => {
    const sortObj = (obj) => {
      if (Array.isArray(obj)) return obj.map(sortObj);
      if (obj && typeof obj === 'object') {
        return Object.keys(obj)
          .sort()
          .reduce((acc, key) => {
            acc[key] = sortObj(obj[key]);
            return acc;
          }, {});
      }
      return obj;
    };
    return JSON.stringify(sortObj(a)) === JSON.stringify(sortObj(b));
  };

  const ASSIGN_PAGE_SIZE = 25;
  const ASSIGN_MIN_SEARCH_LENGTH = 1;

  const handleAssignSearch = useCallback(
    async (inputValue, { page } = {}) => {
      const raw = typeof inputValue === 'string' ? inputValue : inputValue?.search ?? '';
      const trimmed = raw.trim();
      const pageNumber = typeof page === 'number' && page > 0 ? page : 1;
      const shouldLoadDefault = trimmed === '';

      if (!shouldLoadDefault && trimmed.length < ASSIGN_MIN_SEARCH_LENGTH) {
        return { options: [], hasMore: false };
      }

      try {
        const resp = await getPaginatedAssetsAndAreas({
          page: pageNumber,
          pageSize: ASSIGN_PAGE_SIZE,
          search: shouldLoadDefault ? '' : trimmed,
          type: assignType,
        });
        const items = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
        const options = items.map((i) => ({ value: i.id, label: i.label, type: i.type }));
        const totalCount =
          typeof resp?.total === 'number'
            ? resp.total
            : typeof resp?.count === 'number'
              ? resp.count
              : undefined;
        const hasMore =
          typeof resp?.hasMore === 'boolean'
            ? resp.hasMore
            : typeof totalCount === 'number'
              ? pageNumber * ASSIGN_PAGE_SIZE < totalCount
              : options.length === ASSIGN_PAGE_SIZE;

        return {
          options,
          totalCount,
          pageSize: ASSIGN_PAGE_SIZE,
          hasMore,
        };
      } catch (err) {
        console.error('Error fetching assets/areas:', err);
        return { options: [], hasMore: false };
      }
    },
    [assignType],
  );

  const formatAssignOption = (option) => (
    <div className="d-flex justify-content-start align-items-center gap-2">
      <span className="badge bg-secondary ms-2"><Icon icon={option.type === 'Asset' ? "mage:delivery-truck" : "ic:baseline-location-on"} className="icon text-xl line-height-1" /></span>
      <span>{option.label}</span>
    </div>
  );

  const formatOptionColor = (option) => (
    <div className="d-flex align-items-center gap-2">
      <span
        className={`d-inline-block rounded-circle bg-${
          option.colours?.bootstrapName || option.bootstrapName || option.colour || 'secondary'
        }`}
        style={{ width: '10px', height: '10px' }}
      ></span>
      <span>{option.label}</span>
    </div>
  );

  const handleTaskSelectChange = useCallback(
    (opt, setFormData) => {
      const task = opt?.task ?? null;
      const cycleOrder =
        task?.schedulingType === 'cycle' && task?.cycleGroupOrder !== null && task?.cycleGroupOrder !== undefined
          ? task.cycleGroupOrder
          : null;
      const taskName = task?.code?.name || '';
      const modifierCode = task?.modifier?.code || '';
      const generatedTitle = taskName
        ? [taskName, modifierCode].filter(Boolean).join(' - ')
        : null;

      setSelectedTaskOption(opt ?? null);
      setFormData((prev) => ({
        ...prev,
        taskId: task?.id ?? null,
        cycleGroupOrder: cycleOrder,
        ...(!editingId && generatedTitle ? { workOrderTitle: generatedTitle } : {}),
      }));
    },
    [editingId, setSelectedTaskOption],
  );

  // Specify the input fields for the add / edit form
  // Uses the useFromBuilder hook to generate the form fields for the edit modal
  const selectedAssetId = selectedAssign?.type === 'Asset' ? selectedAssign.value : null;

  const crewOptions = useMemo(() => {
    const selectedDepartmentId = Number(newWorkOrder.departmentId);
    const hasSelectedDepartment = Number.isFinite(selectedDepartmentId) && selectedDepartmentId > 0;
    const assignedCrewIds = normalizeIdArray(newWorkOrder.crew);
    const usersById = new Map((Array.isArray(crew) ? crew : []).map((user) => [Number(user?.id), user]));
    const allowedTeamIds = Array.isArray(teamTechnicianIds) ? new Set(teamTechnicianIds) : null;
    const optionsById = new Map();

    (Array.isArray(technicians) ? technicians : []).forEach((technician) => {
      const userId = Number(technician?.userId);
      if (!Number.isFinite(userId)) return;
      if (allowedTeamIds && !allowedTeamIds.has(userId)) return;

      const linkedUser = technician?.users || usersById.get(userId) || null;
      const departmentIds = normalizeIdArray(linkedUser?.departments);
      const hasAllDepartmentAccess = departmentIds.includes(-1);
      const departmentMatch = hasSelectedDepartment
        ? hasAllDepartmentAccess || departmentIds.includes(selectedDepartmentId)
        : false;

      if (!departmentMatch && !assignedCrewIds.includes(userId)) return;

      optionsById.set(userId, {
        value: userId,
        label: linkedUser?.name || technician?.name || `User ${userId}`,
      });
    });

    assignedCrewIds.forEach((userId) => {
      if (optionsById.has(userId)) return;
      const linkedUser = usersById.get(userId);
      optionsById.set(userId, {
        value: userId,
        label: linkedUser?.name || `User ${userId}`,
      });
    });

    return [...optionsById.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [crew, newWorkOrder.crew, newWorkOrder.departmentId, teamTechnicianIds, technicians]);

  const workOrderGeneralFields = useMemo(() => {
    const startDisabled = newWorkOrder.statusId === 1;
    const completeDisabled = [1, 2].includes(newWorkOrder.statusId);
    const scheduleDisabled = false;
    const crewDisabled = !Number.isFinite(Number(newWorkOrder.departmentId)) || Number(newWorkOrder.departmentId) <= 0;
    const fields = [
      /*...(!autoGenWorkOrderNo || editingId
        ? [{ field: "workOrderNumber", label: "Number", type: "text", size: 4, required: true, displayField: "", disabled: disabled }]
        : []),*/
      { field: "workOrderTitle", label: "Title", type: "text", size: 8, required: true, displayField: "" },
      ...(Number(useCostResponsibility) === 1
        ? [{
          field: 'costResponsibilityId',
          label: 'Cost Responsibility',
          type: 'select',
          size: 4,
          required: true,
          api: 'cost-responsibility',
          displayField: 'name',
        }]
        : []),
      { field: "statusId", label: "Status", type: "select", size: 4, required: true, api: "work-order-status", displayField: "name", formatOptionLabel: formatOptionColor },
      { field: "workOrderDescription", label: "Description", type: "textarea", size: 12, required: false, displayField: "", rows: 3 },
      {
        field: "assignable",
        label: "Asset or Area",
        type: "paginated-select",
        size: 4,
        required: true,
        loadOptions: handleAssignSearch,
        selectedOption: selectedAssign,
        setSelectedOption: setSelectedAssign,
        formatOptionLabel: formatAssignOption,
        disabled: Boolean(editingId),
        isClearable: true,
        minSearchLength: ASSIGN_MIN_SEARCH_LENGTH,
      },
      ...(assetTaskOptions.length > 0
        ? [{ field: "taskId", label: "Assign Task", type: "select", size: 4, required: false, options: assetTaskOptions, isClearable: true, selectedOption: selectedTaskOption, onChange: handleTaskSelectChange }]
        : [{ field: "__taskSpacer", label: "", type: "spacer", size: 4 }]),
      { field: "spacer_x", label: "", type: "empty", size: 1 },
      { field: "breakdown", label: "Breakdown", type: "switch", size: 1, required: false, switchColor: 'danger' },
      { field: "statutory", label: "Compliance", type: "switch", size: 1, required: false, switchColor: 'danger' },
      { field: "taskCodeId", label: "Task Code", type: "select", size: 3, required: false, api: "task-codes", displayField: "name", isClearable: true },
      { field: "modifierCodeId", label: "Modifier Code", type: "select", size: 3, required: false, api: "modifier-codes", displayField: "name", isClearable: true },
      { field: "taskTypeId", label: "Task Type", type: "select", size: 3, required: true, api: "task-types", displayField: "taskTypeName" },
      { field: "priorityId", label: "Priority", type: "select", size: 3, required: true, api: "priority", displayField: "name", formatOptionLabel: formatOptionColor },
    ];

    return [
      ...fields,
      ...buildWorkOrderStatusFields({
        newWorkOrder,
        editingId,
        scheduleDisabled,
        startDisabled,
        completeDisabled,
        includeTeams: true,
        formatOptionColor,
        includeCrew: true,
        crewOptions,
        crewDisabled,
        includeWorkOrderImages: true,
        handleImageClick,
      }),
    ];
  }, [
    disabled,
    assignType,
    selectedAssign,
    editingId,
    newWorkOrder.statusId,
    newWorkOrder.departmentId,
    newWorkOrder.workOrderScheduleId,
    crewOptions,
    handleImageClick,
    assetTaskOptions,
    selectedTaskOption,
    handleTaskSelectChange,
    useCostResponsibility,
    formatOptionColor,
  ]);

  const workOrderCompleteFields = useMemo(() => {
    return buildWorkOrderCompleteFields({ newWorkOrder, handleImageClick });
  }, [newWorkOrder.statusId, handleImageClick]);

  const workOrderValidationFields = useMemo(() => {
    return buildWorkOrderValidationFields({
      generalFields: workOrderGeneralFields,
      completeFields: workOrderCompleteFields,
      editingId,
      statusId: newWorkOrder.statusId,
    });
  }, [editingId, newWorkOrder.statusId, workOrderCompleteFields, workOrderGeneralFields]);

  const {
    errors,
    setErrors,
  } = useValidator(workOrderValidationFields, newWorkOrder);

  const combinedErrors = useMemo(
    () => ({ ...errors, ...createEventErrors }),
    [errors, createEventErrors],
  );

  const generalTabHasError = Object.keys(combinedErrors).length > 0;
  const workOrderDocumentFields = useMemo(
    () => [
      {
        field: 'workorderFiles',
        label: 'Documents',
        type: 'files',
        size: 12,
        directory: 'workorderfiles',
        accept:
          '.jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.bmp,.gif,.heic,.dwg,.dxf,.svg',
      },
    ],
    [],
  );

  const openAiModal = () => {
    setAiPromptText('');
    setAiError('');
    setShowAiModal(true);
  };

  const closeAiModal = () => {
    if (!aiLoading) {
      setShowAiModal(false);
    }
  };

  const handleAiCreateWorkOrder = async () => {
    if (!aiPromptText.trim()) {
      setAiError('Please provide work order details.');
      return;
    }
    setAiLoading(true);
    setAiError('');
    try {
      const response = await createWorkOrderWithAi(aiPromptText.trim());
      const aiData = response?.data;
      if (!aiData) {
        setAiError(
          response?.message ||
          'Unable to create work order with AI.',
        );
        return;
      }

      const assetNumber = aiData.assetNumber?.trim();
      if (!assetNumber) {
        setAiError('AI response is missing an asset number.');
        return;
      }

      const asset = await getAssetByAssetNumber(assetNumber);
      if (!asset?.id) {
        setAiError(`Unable to find asset with number "${assetNumber}".`);
        return;
      }

      const initNewWorkOrder = getInitialWorkOrder();

      setNewWorkOrder({
        ...initNewWorkOrder,
        workOrderTitle: aiData.workOrderTitle ?? '',
        workOrderDescription: aiData.workOrderDescription ?? '',
        statusId: Number(aiData.statusId ?? initNewWorkOrder.statusId),
        priorityId: Number(aiData.priority ?? initNewWorkOrder.priorityId),
        taskTypeId: Number(aiData.taskTypeId ?? initNewWorkOrder.taskTypeId),
        taskId: aiData.taskId && Number(aiData.taskId) !== 0
          ? Number(aiData.taskId)
          : null,
        cycleGroupOrder:
          aiData.cycleGroupOrder === null || aiData.cycleGroupOrder === undefined
            ? initNewWorkOrder.cycleGroupOrder
            : Number(aiData.cycleGroupOrder),
        scheduledAt: aiData.scheduledAt ?? null,
        dueDate: aiData.dueDate ?? null,
        statutory: Number(aiData.statutory ?? initNewWorkOrder.statutory),
        breakdown: Number(aiData.breakdown ?? 0),
      });
      setSelectedAssign({
        value: asset.id,
        label: `${asset.assetNumber || assetNumber} - ${asset.assetDescription || ''}`.trim(),
        type: 'Asset',
      });
      setIsAdvancedAsset(false);
      setEditingId(null);
      setFormReadOnly(false);
      setWorkOrderParts([]);
      durationEditedRef.current = false;
      actualDurationEditedRef.current = false;
      setActualDuration('');
      prevStartedAtRef.current = null;
      setShowAiModal(false);
      setShowModal(true);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Unable to create work order with AI.';
      setAiError(message);
    } finally {
      setAiLoading(false);
    }
  };

  const labourValidationFields = useMemo(
    () => [
      { field: 'tradeId', label: 'Trade', required: true },
      { field: 'defaultExpenseCode', label: 'Expense Code', required: true },
      { field: 'labourHours', label: 'Hours', required: true },
      { field: 'labourRate', label: 'Labour Rate', required: true },
      { field: 'qty', label: 'Quantity', required: true },
    ],
    [],
  );

  const {
    errors: labourErrors,
    setErrors: setLabourErrors,
    validate: validateLabour,
  } = useValidator(labourValidationFields, labourForm);

  const clearEquipmentFieldError = useCallback(
    (field) => {
      setEquipmentErrors((prev) => {
        if (!prev || !prev[field]) return prev;
        const { [field]: _removed, ...rest } = prev;
        return rest;
      });
    },
    [setEquipmentErrors],
  );

  const clearLabourFieldError = useCallback(
    (field) => {
      setLabourErrors((prev) => {
        if (!prev || !prev[field]) return prev;
        const next = { ...prev };
        delete next[field];
        return next;
      });
    },
    [setLabourErrors],
  );

  const tableFilters = useMemo(() => [
    { field: "departmentId", type: "dropDown", label: "Site or Department", api: "hierarchy-structures", displayField: "name", menuBarFilter: 1 },
    { field: "assetId", type: "dropDown", label: "Asset", api: "assets/for-dropdown", displayField: (a) => `${a.unitNumber || a.assetNumber || ''} ${a.assetDescription || ''}`, menuBarFilter: 1 },
    { field: "taskTypeId", type: "dropDown", label: "Task Type", api: "task-types", displayField: "taskTypeName", menuBarFilter: 1 },
    {
      field: "scheduledWithinDays",
      type: "dropDown",
      label: "Scheduled Within",
      isMulti: false,
      options: [
        { value: 1, label: "Next 24 Hours" },
        { value: 7, label: "Next 7 Days" },
        { value: 14, label: "Next 14 Days" },
        { value: 30, label: "Next 30 Days" },
        { value: 60, label: "Next 60 Days" },
        { value: 90, label: "Next 90 Days" },
        { value: 180, label: "Next 180 Days" },
        { value: "custom", label: "Custom Date Range" },
      ],
      menuBarFilter: 0,
      maxMenuHeight: 330,
      renderAfter: renderScheduledWithinAfter,
    },
    {
      field: "actualWithinDays",
      type: "dropDown",
      label: "Actual Within",
      isMulti: false,
      options: [
        { value: 1, label: "Last 24 Hours" },
        { value: 7, label: "Last 7 Days" },
        { value: 14, label: "Last 14 Days" },
        { value: 30, label: "Last 30 Days" },
        { value: 60, label: "Last 60 Days" },
        { value: 90, label: "Last 90 Days" },
        { value: 180, label: "Last 180 Days" },
        { value: "custom", label: "Custom Date Range" },
      ],
      menuBarFilter: 0,
      maxMenuHeight: 330,
      renderAfter: renderActualWithinAfter,
    },
    { field: "taskCodeId", type: "dropDown", label: "Task Code", api: "task-codes", displayField: "name" },
    {
      field: "linkedTasks",
      type: "dropDown",
      label: "Linked Tasks",
      isMulti: false,
      options: [
        { value: "both", label: "Both" },
        { value: "linked", label: "Linked to a Task" },
        { value: "unlinked", label: "Not Linked to a Task" },
      ],
      menuBarFilter: 0,
    },
    // { field: "assetTypeId", type: "dropDown", label: "Asset Type", api: "asset-type", displayField: "name" },
    { field: "assetClassId", type: "dropDown", label: "Asset Class", api: "class", displayField: "className", menuBarFilter: 0 },
    { field: "assetGroupId", type: "dropDown", label: "Asset Group", api: "groups", displayField: "groupName", menuBarFilter: 0 },
    { field: "teamsId", type: "dropDown", label: "Team", api: "teams", displayField: "teamName" },
    { field: "crewId", type: "dropDown", label: "Crew Member", api: "users", displayField: "name" },
    // { field: "areaId", type: "dropDown", label: "Area", api: "areas", displayField: "name" },
    { field: "statusId", type: "dropDown", label: "Work Order Status", api: "work-order-status", displayField: "name" },
    { field: "priorityId", type: "dropDown", label: "Priority", api: "priority", displayField: "name", formatOptionLabel: formatOptionColor },
    // { statusId: [1, 2, 3, 4], linkedTasks: "both" },
    { field: "statutory", type: "switch", label: "Compliance Task", api: "", displayField: "" },
    { field: "overdueOnly", type: "switch", label: "Overdue", api: "", displayField: "", menuBarFilter: 0 },
    { field: "jobReadyStatus", type: "switch", label: "Job Ready", api: "", displayField: "", menuBarFilter: 0 },
  ], [renderScheduledWithinAfter, renderActualWithinAfter]);

  const { renderField: renderGeneralFields } = useFormBuilder(
    workOrderGeneralFields,
    newWorkOrder,
    setNewWorkOrder,
    combinedErrors,
  );
  const { renderField: renderCompleteFields } = useFormBuilder(
    workOrderCompleteFields,
    newWorkOrder,
    setNewWorkOrder,
    combinedErrors,
  );
  const { renderField: renderProgressFields } = useFormBuilder(
    workOrderCompleteFields,
    newWorkOrder,
    setNewWorkOrder,
    combinedErrors,
  );
  const { renderField: renderDocumentFields } = useFormBuilder(
    workOrderDocumentFields,
    newWorkOrder,
    setNewWorkOrder,
    errors,
  );

  const initialWoFilterValues = useMemo(() => {
    const initial = {};
    if (initialStartDateParam || initialEndDateParam) {
      initial.actualWithinDays = 'custom';
    }
    if (initialStartDateParam || initialEndDateParam || initialCompletedParam || initialTaskTypeParam || initialComplianceParam) {
      initial.linkedTasks = 'linked';
    }
    return initial;
  }, [initialStartDateParam, initialEndDateParam, initialCompletedParam, initialTaskTypeParam, initialComplianceParam]);

  const {
    appliedFilters,
    appliedFilterParams,
    tempFilters,
    renderFilterControls,
    renderMenuBarFilters,
    resetFilters,
    filterOptions,
    setTempFiltersBatch,
  } = useTableFilters(
    tableFilters,
    initialWoFilterValues,
    {
      onReset: () => setSearchTerm(''),
      onClearFilters: clearFilterQueryParams,
    }
  );

  // When loading with completed=true URL param, switch panel to "Complete" tab
  // so that assignmentFilter='complete' is sent instead of the default 'unassigned',
  // which would contradict the statusId=4 filter and exclude completed work orders.
  const initialPanelAssignmentAppliedRef = useRef(false);
  useEffect(() => {
    if (initialPanelAssignmentAppliedRef.current) return;
    if (initialCompletedParam !== 'true') return;
    initialPanelAssignmentAppliedRef.current = true;
    setPanelAssignmentFilter('complete');
  }, [initialCompletedParam]);

  const initialCompletedAppliedRef = useRef(false);
  const statusIdOptions = filterOptions['statusId'];
  useEffect(() => {
    if (initialCompletedAppliedRef.current) return;
    if (initialCompletedParam !== 'true') return;
    if (!statusIdOptions || !statusIdOptions.length) return;

    initialCompletedAppliedRef.current = true;
    const completedOption = statusIdOptions.find(
      (opt) => String(opt.label || '').trim().toLowerCase().startsWith('complet'),
    );
    if (completedOption) {
      setTempFiltersBatch({ statusId: [completedOption.value] }, { apply: true });
    }
  }, [statusIdOptions]);

  const initialTaskTypeAppliedRef = useRef(false);
  const taskTypeIdOptions = filterOptions['taskTypeId'];
  useEffect(() => {
    if (initialTaskTypeAppliedRef.current) return;
    if (!initialTaskTypeParam) return;
    if (!taskTypeIdOptions?.length) return;

    const cachedData = getCache('task-types');
    const rawArr = Array.isArray(cachedData) ? cachedData
      : Array.isArray(cachedData?.data) ? cachedData.data : [];
    if (!rawArr.length) return;

    const matchingIds = rawArr
      .filter((t) => t[initialTaskTypeParam] === 1)
      .map((t) => t.id);

    if (matchingIds.length > 0) {
      initialTaskTypeAppliedRef.current = true;
      setTempFiltersBatch({ taskTypeId: matchingIds }, { apply: true });
    }
  }, [taskTypeIdOptions]);

  const initialComplianceAppliedRef = useRef(false);
  useEffect(() => {
    if (initialComplianceAppliedRef.current) return;
    if (initialComplianceParam !== 'true') return;
    initialComplianceAppliedRef.current = true;
    setTempFiltersBatch({ statutory: true }, { apply: true });
  }, [initialComplianceParam]);

  const initialDepartmentIdsAppliedRef = useRef(false);
  const departmentIdOptions = filterOptions['departmentId'];
  useEffect(() => {
    if (initialDepartmentIdsAppliedRef.current) return;
    if (!initialDepartmentIdsParam) return;
    if (!departmentIdOptions?.length) return;

    initialDepartmentIdsAppliedRef.current = true;
    const ids = initialDepartmentIdsParam
      .split(',')
      .map((s) => Number(s.trim()))
      .filter((n) => !isNaN(n) && n > 0);
    if (ids.length > 0) {
      setTempFiltersBatch({ departmentId: ids }, { apply: true });
    }
  }, [initialDepartmentIdsParam, departmentIdOptions]);

  const resolvedFilterParams = useMemo(() => {
    const params = { ...appliedFilterParams };
    if (dateFilterTypeParam) {
      params.dateFilterType = dateFilterTypeParam;
    }
    if (dataFilterTypeParam) {
      params.dataFilterType = dataFilterTypeParam;
    }
    if (dataFromElementParam) {
      params.dataFromElement = dataFromElementParam;
    }
    if (params.scheduledWithinDays === "custom") {
      delete params.scheduledWithinDays;
      params.scheduledAtStart = customScheduledStart;
      params.scheduledAtEnd = customScheduledEnd;
    } else if (
      params.scheduledWithinDays === null ||
      params.scheduledWithinDays === undefined ||
      params.scheduledWithinDays === ''
    ) {
      delete params.scheduledWithinDays;
    }
    if (params.actualWithinDays === "custom") {
      delete params.actualWithinDays;
      params.actualAtStart = customActualStart;
      params.actualAtEnd = customActualEnd;
    } else if (
      params.actualWithinDays === null ||
      params.actualWithinDays === undefined ||
      params.actualWithinDays === ''
    ) {
      delete params.actualWithinDays;
    }
    if (
      params.linkedTasks === null ||
      params.linkedTasks === undefined ||
      params.linkedTasks === '' ||
      params.linkedTasks === 'both'
    ) {
      delete params.linkedTasks;
    }
    return params;
  }, [
    appliedFilterParams,
    dateFilterTypeParam,
    dataFilterTypeParam,
    dataFromElementParam,
    customScheduledStart,
    customScheduledEnd,
    customActualStart,
    customActualEnd,
  ]);

  const resetWorkOrderPagination = useCallback(() => {
    setWorkOrders([]);
    setWoPage(1);
    setWoHasMore(true);
  }, []);

  const handlePanelAssignmentChange = useCallback(
    (value) => {
      if (panelAssignmentFilter === value) {
        return;
      }
      setPanelAssignmentFilter(value);
      setSelectedWorkOrder(null);
      if (listContainerRef.current) {
        listContainerRef.current.scrollTop = 0;
      }
      resetWorkOrderPagination();
    },
    [listContainerRef, panelAssignmentFilter, resetWorkOrderPagination],
  );

  const handlePanelSortChange = useCallback(
    (value) => {
      if (panelSortOption === value) {
        return;
      }
      setPanelSortOption(value);
      if (listContainerRef.current) {
        listContainerRef.current.scrollTop = 0;
      }
      resetWorkOrderPagination();
    },
    [listContainerRef, panelSortOption, resetWorkOrderPagination],
  );

  const updateWorkOrders = useCallback(async () => {
    resetWorkOrderPagination();
    const btn = document.getElementById('apply-filters-btn');
    if (btn) btn.click();
  }, [resetWorkOrderPagination]);

  useEffect(() => {
    const handleExternalWorkOrderRefresh = (event) => {
      const { workOrderIds, eventId } = event?.detail || {};
      updateWorkOrders();
      if (Array.isArray(workOrderIds) && workOrderIds.includes(selectedWorkOrder?.id) && eventId) {
        setSelectedWorkOrder((prev) => (prev ? { ...prev, eventId } : prev));
      }
    };

    window.addEventListener('workorders:refresh', handleExternalWorkOrderRefresh);
    return () => window.removeEventListener('workorders:refresh', handleExternalWorkOrderRefresh);
  }, [selectedWorkOrder?.id, updateWorkOrders]);

  const formatTaskCode = (task) => {
    const raw =
      task?.code?.name ??
      (typeof task?.code === 'string' ? task.code : undefined) ??
      task?.codeId ??
      '';
    return raw !== null && raw !== undefined ? String(raw) : '';
  };

  useEffect(() => {
    fetchData(getAll, { apiName: 'users' }, setCrew);
  }, []);

  useEffect(() => {
    getTechnicians()
      .then((list) => {
        setTechnicians(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        console.error('Error loading technicians', err);
        setTechnicians([]);
      });
  }, []);

  useEffect(() => {
    if (!newWorkOrder.teamsId) {
      setTeamTechnicianIds(null);
      return;
    }

    getTeamsById(newWorkOrder.teamsId)
      .then((team) => {
        const ids = (Array.isArray(team?.technicians) ? team.technicians : [])
          .map((technician) => Number(technician?.userId))
          .filter((id) => Number.isFinite(id));
        setTeamTechnicianIds(ids);
      })
      .catch((err) => {
        console.error('Error loading team technicians', err);
        setTeamTechnicianIds([]);
      });
  }, [newWorkOrder.teamsId]);

  useEffect(() => {
    if (!initialWorkOrderParam) return;
    resetFilters();
    setSearchTerm(initialWorkOrderParam);
    setSelectedWorkOrder(null);
    setNotFound(false);
    updateWorkOrders();
  }, [initialWorkOrderParam]);


  useEffect(() => {
    const state = location.state;
    if (!state || typeof state !== 'object') return;

    const assetIdFromState = Number(state.prefillAssetId ?? state.assetId);
    if (!Number.isFinite(assetIdFromState) || assetIdFromState <= 0) {
      navigate(location.pathname + location.search, { replace: true, state: null });
      return;
    }

    const consumeRouteState = () => {
      navigate(location.pathname + location.search, { replace: true, state: null });
    };

    if (state.createOnLoad) {
      let cancelled = false;
      (async () => {
        setModalLoading(true);
        try {
          const asset = await getAssetById(assetIdFromState);
          if (cancelled) return;
          const initNewWorkOrder = getInitialWorkOrder();
          setNewWorkOrder(initNewWorkOrder);
          setSelectedAssign({
            value: assetIdFromState,
            label: `${asset?.assetNumber || ''} ${asset?.assetDescription || ''}`.trim() || `Asset ${assetIdFromState}`,
            type: 'Asset',
          });
          setAssignType('asset');
          setIsAdvancedAsset(false);
          setEditingId(null);
          setFormReadOnly(false);
          setWorkOrderParts([]);
          setWorkOrderCostItems([]);
          durationEditedRef.current = false;
          actualDurationEditedRef.current = false;
          setActualDuration('');
          setActualPartsCost('');
          setActualLabourCost('');
          setActualMiscCost('');
          setActualLabourHours('');
          prevStartedAtRef.current = null;
          setShowModal(true);
        } catch (err) {
          console.error('Error pre-filling work order asset from route state', err);
        } finally {
          if (!cancelled) {
            setModalLoading(false);
            consumeRouteState();
          }          
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    getAssetById(assetIdFromState)
      .then((asset) => {
        setSearchTerm(asset?.assetNumber || String(assetIdFromState));
      })
      .catch((err) => {
        console.error('Error pre-filtering work orders by asset from route state', err);
        setSearchTerm(String(assetIdFromState));
      })
      .finally(() => {
        consumeRouteState();
      });
  }, [location.pathname, location.search, location.state, navigate]);


  useEffect(() => {
    fetchData(getTrades, {}, setTradeOptions);
    fetchData(getExpenseCodes, {}, setExpenseOptions);
    fetchData(getExternalServices, {}, setServiceOptions);
  }, []);

  useEffect(() => {
    if (!selectedAssign) {
      setSelectedAssetDetails(null);
      setAssetTaskOptions([]);
      setAssetTasksById({});
      setSelectedTaskOption(null);
      setNewWorkOrder((prev) =>
        prev.taskId || prev.cycleGroupOrder
          ? { ...prev, taskId: null, cycleGroupOrder: null }
          : prev,
      );
      return;
    }
    if (selectedAssign.type === 'Asset') {
      setNewWorkOrder((prev) => ({
        ...prev,
        assetId: selectedAssign.value,
        areaId: null,
        assignable: selectedAssign,
      }));
      getAssetById(selectedAssign.value)
        .then((asset) => {
          setSelectedAssetDetails(asset);
          if (!editingId && asset?.assetDepartmentId) {
            setNewWorkOrder((prev) => ({ ...prev, departmentId: asset.assetDepartmentId }));
          }
          setIsAdvancedAsset(asset?.PlanningType?.typeDescription?.toLowerCase() === 'advanced');
        })
        .catch((err) => {
          console.error('Error loading asset department', err);
          setSelectedAssetDetails(null);
          setIsAdvancedAsset(false);
        });
    } else {
      setSelectedAssetDetails(null);
      setNewWorkOrder((prev) => ({
        ...prev,
        areaId: selectedAssign.value,
        assetId: null,
        assignable: selectedAssign,
      }));
      if (!editingId) {
        getAreaById(selectedAssign.value)
          .then((area) => {
            if (area?.departmentId) {
              setNewWorkOrder((prev) => ({ ...prev, departmentId: area.departmentId }));
            }
          })
          .catch((err) => console.error('Error loading area department', err));
      }
      setIsAdvancedAsset(false);
      setAssetTaskOptions([]);
      setSelectedTaskOption(null);
      setNewWorkOrder((prev) =>
        prev.taskId || prev.cycleGroupOrder
          ? { ...prev, taskId: null, cycleGroupOrder: null }
          : prev,
      );
    }
  }, [selectedAssign, editingId]);

  useEffect(() => {
    let cancelled = false;

    if (!selectedAssetId) {
      setAssetTaskOptions([]);
      setAssetTasksById({});
      setSelectedTaskOption(null);
      setNewWorkOrder((prev) =>
        prev.taskId || prev.cycleGroupOrder
          ? { ...prev, taskId: null, cycleGroupOrder: null }
          : prev,
      );
      return undefined;
    }

    const loadTasks = async () => {
      try {
        const response = await getWorkOrderTasksByAsset(selectedAssetId);
        if (cancelled) return;
        const tasks = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];
        const details = {};
        const options = tasks.map((task) => {
          details[task.id] = task;
          const modifierCodeName =
            task.modifier?.code ||
            '';
          const taskCodeName = task.code?.name || task.name;
          const baseLabel =
            [
              task.code?.name ? `${task.code.code} - ${taskCodeName}` : taskCodeName,
              modifierCodeName,
            ]
              .filter(Boolean)
              .join(' - ') || `Task ${task.id}`;
          const label =
            task.schedulingType === 'cycle' && task.cycleGroupOrder !== null && task.cycleGroupOrder !== undefined
              ? `${baseLabel} [${task.cycleGroupOrder}]`
              : baseLabel;

          return {
            value: `${task.id}-${task.cycleGroupOrder ?? 'none'}`,
            label,
            task,
          };
        });
        setAssetTaskOptions(options);
        setAssetTasksById(details);
        setNewWorkOrder((prev) => {
          if (
            prev.taskId &&
            !options.some(
              (opt) =>
                Number(opt.task?.id) === Number(prev.taskId) &&
                (opt.task?.schedulingType !== 'cycle'
                  ? true
                  : (opt.task?.cycleGroupOrder ?? null) === (prev.cycleGroupOrder ?? null)),
            )
          ) {
            return { ...prev, taskId: null, cycleGroupOrder: null };
          }
          return prev;
        });
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading tasks for asset', err);
        setAssetTaskOptions([]);
        setAssetTasksById({});
        setSelectedTaskOption(null);
        setNewWorkOrder((prev) =>
          prev.taskId || prev.cycleGroupOrder
            ? { ...prev, taskId: null, cycleGroupOrder: null }
            : prev,
        );
      }
    };

    loadTasks();

    return () => {
      cancelled = true;
    };
  }, [selectedAssetId]);

  useEffect(() => {
    const rawTaskId = newWorkOrder.taskId;
    const numericTaskId = Number(rawTaskId);
    if (!assetTaskOptions.length || rawTaskId === null || rawTaskId === undefined || rawTaskId === '') {
      if (selectedTaskOption !== null) {
        setSelectedTaskOption(null);
      }
      return;
    }
    if (Number.isNaN(numericTaskId)) {
      return;
    }

    const desiredOrder = newWorkOrder.cycleGroupOrder ?? null;
    const match = assetTaskOptions.find((opt) => {
      const optionTaskId = Number(opt?.task?.id ?? opt?.taskId ?? null);
      if (optionTaskId !== numericTaskId) return false;
      if (opt?.task?.schedulingType === 'cycle') {
        return (opt.task?.cycleGroupOrder ?? null) === (desiredOrder ?? null);
      }
      return true;
    });

    if (match || selectedTaskOption !== null) {
      setSelectedTaskOption(match ?? null);
    }
  }, [assetTaskOptions, newWorkOrder.cycleGroupOrder, newWorkOrder.taskId, selectedTaskOption]);

  useEffect(() => {
    const rawTaskId = newWorkOrder.taskId;
    if (rawTaskId === null || rawTaskId === undefined || rawTaskId === '') return;
    const task = assetTasksById?.[rawTaskId];
    if (!task) return;

    setNewWorkOrder((prev) => {
      const updates = {};
      if (
        task.taskTypeId !== undefined &&
        task.taskTypeId !== null &&
        prev.taskTypeId !== task.taskTypeId
      ) {
        updates.taskTypeId = task.taskTypeId;
      }
      if (
        task.code !== undefined &&
        task.code !== null
      ) {
        updates.taskCodeId = task.code.id;
      } else {
        updates.taskCodeId = null;
      }
      const taskModifierCodeId = task.modifier?.id ?? null;
      if (prev.modifierCodeId !== taskModifierCodeId) {
        updates.modifierCodeId = taskModifierCodeId;
      }
      if (
        task.costResponsibilityId !== undefined &&
        task.costResponsibilityId !== null &&
        prev.costResponsibilityId !== task.costResponsibilityId
      ) {
        updates.costResponsibilityId = task.costResponsibilityId;
      }
      if (Object.keys(updates).length === 0) {

        return prev;
      }
      return { ...prev, ...updates };
    });
  }, [assetTasksById, newWorkOrder.taskId, setNewWorkOrder]);

  useEffect(() => {
    if (!editingId) return;

    const taskId = Number(newWorkOrder?.taskId) || null;

    if (!taskId || Number.isNaN(taskId)) {
      prevTaskIdRef.current = null;
      return;
    }

    const isInitialSelection = !taskSelectionInitializedRef.current;
    if (isInitialSelection) {
      taskSelectionInitializedRef.current = true;
      if (editingId) {
        prevTaskIdRef.current = taskId;
        return;
      }
    }

    if (prevTaskIdRef.current === taskId) {
      return;
    }

    prevTaskIdRef.current = taskId;
    let cancelled = false;

    const ensureUniqueIds = (current, idsToAdd) => {
      if (!Array.isArray(idsToAdd) || idsToAdd.length === 0) return current;
      const merged = new Set(current || []);
      idsToAdd.forEach((id) => {
        if (id) {
          merged.add(id);
        }
      });
      return Array.from(merged);
    };

    const loadTaskBillingDetails = async () => {
      try {
        const [materials, labour, services, equipment] = await Promise.all([
          getBillOfMaterialsByTaskId(taskId),
          getBillOfLabourByTaskId(taskId),
          getBillOfServicesByTaskId(taskId),
          getBillOfEquipmentByTaskId(taskId),
        ]);

        if (cancelled) return;

        const mappedItems = mapTaskBillingToWorkOrderItems({
          materials,
          labour,
          services,
          equipment,
          workOrderId: editingId || newWorkOrder.id || null,
        });

        const existingPartIds = (workOrderPartsRef.current || [])
          .map((part) => part?.id)
          .filter(Boolean);
        const existingLabourIds = (workOrderLabourRef.current || [])
          .map((lb) => lb?.id)
          .filter(Boolean);
        const existingServiceIds = (workOrderServicesRef.current || [])
          .map((svc) => svc?.id)
          .filter(Boolean);
        const existingEquipmentIds = (workOrderEquipmentRef.current || [])
          .map((eq) => eq?.id)
          .filter(Boolean);

        if (existingPartIds.length) {
          setDeletedPartIds((prev) => ensureUniqueIds(prev, existingPartIds));
        }
        if (existingLabourIds.length) {
          setDeletedLabourIds((prev) => ensureUniqueIds(prev, existingLabourIds));
        }
        if (existingServiceIds.length) {
          setDeletedServiceIds((prev) => ensureUniqueIds(prev, existingServiceIds));
        }
        if (existingEquipmentIds.length) {
          setDeletedEquipmentIds((prev) => ensureUniqueIds(prev, existingEquipmentIds));
        }

        setWorkOrderParts(mappedItems.parts);
        setWorkOrderLabour(mappedItems.labour);
        setWorkOrderServices(mappedItems.services);
        setWorkOrderEquipment(mappedItems.equipment);
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading task items for work order', err);
        }
      }
    };

    loadTaskBillingDetails();

    return () => {
      cancelled = true;
    };
  }, [editingId, newWorkOrder.id, newWorkOrder.taskId, showModal]);

  useEffect(() => {
    const rawTaskId = newWorkOrder.taskId;
    if (rawTaskId === null || rawTaskId === undefined || rawTaskId === '') return;
    if (!newWorkOrder.scheduledAt) return;

    const task = assetTasksById?.[rawTaskId];
    if (!task || task.duration === undefined || task.duration === null) return;

    const numericDuration = Number(task.duration);
    if (Number.isNaN(numericDuration)) {
      return;
    }

    const formattedDuration =
      typeof task.duration === 'string' ? task.duration : numericDuration.toString();
    setDuration((prev) => {
      if (prev === formattedDuration) {
        return prev;
      }
      durationEditedRef.current = true;
      return formattedDuration;
    });
  }, [assetTasksById, newWorkOrder.scheduledAt, newWorkOrder.taskId]);

  useEffect(() => {
    resetWorkOrderPagination();
  }, [appliedFilters, searchTerm, resetWorkOrderPagination]);

  useEffect(() => {
    setSelectedWorkOrder(null);
    resetWorkOrderPagination();
  }, [viewMode, resetWorkOrderPagination]);

  const handleSortChange = useCallback(
    (field) => {
      if (listContainerRef.current) {
        listContainerRef.current.scrollTop = 0;
      }
      resetWorkOrderPagination();
      if (sortField === field) {
        if (sortOrder === 'asc') {
          setSortOrder('desc');
        } else if (sortOrder === 'desc') {
          setSortField(null);
          setSortOrder(null);
        } else {
          setSortOrder('asc');
        }
      } else {
        setSortField(field);
        setSortOrder('asc');
      }
    },
    [listContainerRef, resetWorkOrderPagination, sortField, sortOrder],
  );

  useEffect(() => {
    const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
    tooltipTriggerList.map((el) => new Tooltip(el));
  }, []);

  useEffect(() => {
    if (!initialWorkOrderParam) return;
    if (searchTerm !== initialWorkOrderParam) return;
    if (woLoading) return;

    if (workOrders.length === 0) {
      setNotFound(true);
      return;
    }
    const match = workOrders.find((w) => w.workOrderNumber === initialWorkOrderParam);
    if (match) {
      setSelectedWorkOrder(match);
      setNotFound(false);
    } else {
      setNotFound(true);
    }
  }, [workOrders, woLoading, initialWorkOrderParam, searchTerm]);

  useEffect(() => {
    if (!initialWorkOrderParam) return;
    if (searchTerm !== initialWorkOrderParam) setNotFound(false);
  }, [searchTerm, initialWorkOrderParam]);

  useEffect(() => {
    let cancelled = false;

    const isPanelView = viewMode === 'panel';
    const effectiveSortField = isPanelView ? panelSortField : sortField;
    const effectiveSortOrder = isPanelView ? panelSortOrder : sortOrder;
    const assignmentFilter = isPanelView ? panelAssignmentFilter : undefined;

    const fetchWorkOrders = async () => {
      setWoLoading(true);
      try {
        const firstPageSize = 100;
        const subsequentPageSize = 5;
        const pageSize = woPage === 1 ? firstPageSize : subsequentPageSize;
        const page = woPage === 1 ? 1 : woPage + (firstPageSize / subsequentPageSize) - 1;
        const resp = await getPaginatedWorkOrders({
          page,
          pageSize,
          search: searchTerm,
          ...resolvedFilterParams,
          sortField: effectiveSortField || undefined,
          sortOrder: effectiveSortOrder || undefined,
          assignmentFilter,
        });
        if (cancelled) return;
        const filtered = appliedFilters.overdueOnly
          ? resp.data.filter((row) => {
            const dueDate = row.dueDate ? new Date(row.dueDate) : null;
            return dueDate && dueDate < new Date() && ![4, 5, 6].includes(row.statusId);
          })
          : resp.data;
        setWorkOrders((prev) => {
          const existingIds = new Set(prev.map((p) => p.id));
          const newItems = filtered.filter((item) => !existingIds.has(item.id));
          const updated = [...prev, ...newItems];
          setWoHasMore(updated.length < resp.total);
          return updated;
        });
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching work orders:', err);
        }
      } finally {
        if (!cancelled) {
          setWoLoading(false);
        }
      }
    };

    fetchWorkOrders();

    return () => {
      cancelled = true;
    };
  }, [
    woPage,
    appliedFilters,
    resolvedFilterParams,
    searchTerm,
    sortField,
    sortOrder,
    viewMode,
    panelAssignmentFilter,
    panelSortField,
    panelSortOrder,
  ]);

  useEffect(() => {
    const container = listContainerRef.current;
    if (!container) return;

    const onScroll = () => {
      if (
        container.scrollTop + container.clientHeight >= container.scrollHeight - 5 &&
        woHasMore &&
        !woLoading
      ) {
        setWoPage((p) => p + 1);
      }
    };

    container.addEventListener('scroll', onScroll);
    return () => container.removeEventListener('scroll', onScroll);
  }, [woHasMore, woLoading, viewMode]);

  useEffect(() => {
    if (viewMode !== 'panel') return;
    if (filteredWorkOrders.length === 0) {
      if (selectedWorkOrder) {
        setSelectedWorkOrder(null);
      }
      return;
    }

    if (!selectedWorkOrder) {
      setSelectedWorkOrder(filteredWorkOrders[0]);
      return;
    }

    const exists = filteredWorkOrders.some((wo) => wo.id === selectedWorkOrder.id);
    if (!exists) {
      setSelectedWorkOrder(filteredWorkOrders[0]);
    }
  }, [viewMode, filteredWorkOrders, selectedWorkOrder]);

  useEffect(() => {
    if (docDtRef.current) {
      docDtRef.current.destroy();
      docDtRef.current = null;
    }
    if (Array.isArray(newWorkOrder.workorderFiles) && newWorkOrder.workorderFiles.length > 0) {
      docDtRef.current = $(docTableRef.current).DataTable({
        paging: false,
        searching: false,
        info: false,
        ordering: false,
        dom: 't',
        language: { paginate: { first: '', last: '', next: '', previous: '' } },
      });
    }
    return () => {
      if (docDtRef.current) {
        docDtRef.current.destroy(true);
        docDtRef.current = null;
      }
    };
  }, [newWorkOrder.workorderFiles]);


  useEffect(() => {

    if (viewMode !== 'panel' || !selectedWorkOrder) return;
    fetchWorkOrderDetails(selectedWorkOrder);
  }, [viewMode, selectedWorkOrder]);

  useEffect(() => {
    const statusId = Number(newWorkOrder.statusId);
    const prevStatusId = prevStatusIdRef.current;
    if (prevStatusId === null) {
      prevStatusIdRef.current = statusId;
      return;
    }

    if (statusId === 2 && prevStatusId !== 2) {
      const now = new Date();

      let startedAtDate = newWorkOrder.scheduledAt
        ? new Date(newWorkOrder.scheduledAt)
        : new Date();

      if (Number.isNaN(startedAtDate.getTime())) {
        startedAtDate = new Date();
      }

      if (startedAtDate > now) {
        startedAtDate = now;
      }

      let savedDuration = parseFloat(duration);
      if (!Number.isFinite(savedDuration) || savedDuration < 0) {
        savedDuration = 1;
      }

      const startedAtIso = startedAtDate.toISOString();

      let dueDate = new Date(
        startedAtDate.getTime() + savedDuration * 60 * 60 * 1000
      );

      if (Number.isNaN(dueDate.getTime()) || dueDate < now) {
        dueDate = new Date(now.getTime() + 5 * 60 * 1000);
      }

      const dueDateIso = dueDate.toISOString();

      setNewWorkOrder((prev) => ({
        ...prev,
        startedAt: startedAtIso,
        dueDate: dueDateIso,
      }));

      if (!Number.isFinite(savedDuration) || savedDuration < 0) {
        setDuration(String(savedDuration));
      }
      durationEditedRef.current = false;
      prevScheduledAtRef.current = startedAtIso;
    }
    prevStatusIdRef.current = statusId;
  }, [newWorkOrder.scheduledAt, newWorkOrder.statusId]);



  // Update duration field when scheduled start or end change
  useEffect(() => {
    const statusId = Number(newWorkOrder.statusId);
    const durationBase = statusId === 2 ? newWorkOrder.startedAt : newWorkOrder.scheduledAt;
    if (!durationBase || !newWorkOrder.dueDate) return;

    const start = new Date(durationBase);
    const end = new Date(newWorkOrder.dueDate);
    const diffMs = end - start;
    if (!Number.isNaN(diffMs)) {
      const hrs = Math.max(0, diffMs / 3600000);
      const val = hrs.toFixed(2);
      if (!durationEditedRef.current && val !== duration) {
        setDuration(val);
      }
    }

    setErrors((prev) => {
      if (statusId === 1 || statusId === 5) {
        if (end <= start) {
          return { ...prev, dueDate: 'Due date must be after scheduled start' };
        }
      }
      if (statusId === 2) {
        if (end <= start) {
          return { ...prev, dueDate: 'Estimated completion must be after started time' };
        }
      }
      const { dueDate, ...rest } = prev;
      return rest;
    });
  }, [duration, newWorkOrder.dueDate, newWorkOrder.scheduledAt, newWorkOrder.startedAt, newWorkOrder.statusId]);

  // Update scheduled end when duration or start changes
  useEffect(() => {
    const statusId = Number(newWorkOrder.statusId);
    const durationBase = statusId === 2 ? newWorkOrder.startedAt : newWorkOrder.scheduledAt;
    if (!durationBase || duration === '') return;
    if (!durationEditedRef.current && prevScheduledAtRef.current === durationBase) return;

    const start = new Date(durationBase);
    const hrs = parseFloat(duration);
    if (Number.isNaN(hrs)) return;
    const end = new Date(start.getTime() + hrs * 3600000);
    const iso = end.toISOString();
    if (newWorkOrder.dueDate !== iso) {
      setNewWorkOrder(prev => ({ ...prev, dueDate: iso }));
    }
    prevScheduledAtRef.current = durationBase;
  }, [duration, newWorkOrder.scheduledAt, newWorkOrder.startedAt, newWorkOrder.statusId, newWorkOrder.dueDate]);

  useEffect(() => {
    if (!newWorkOrder.startedAt || !newWorkOrder.completedAt) {
      if (!actualDurationEditedRef.current) {
        setActualDuration('');
      }
      actualDurationEditedRef.current = false;
      setErrors((prev) => {
        const { completedAt, ...rest } = prev;
        return rest;
      });
      return;
    }

    const start = new Date(newWorkOrder.startedAt);
    const end = new Date(newWorkOrder.completedAt);
    if (isNaN(start) || isNaN(end)) return;
    if (end < start) {
      setErrors((prev) => ({ ...prev, completedAt: 'Completed time cannot be before start time' }));
      return;
    }
    setErrors((prev) => {
      const { completedAt, ...rest } = prev;
      return rest;
    });
    const diffMs = end - start;
    const hrs = Math.max(0, diffMs / 3600000);
    const val = hrs.toFixed(2);
    if (!actualDurationEditedRef.current && val !== actualDuration) {
      setActualDuration(val);
    }
  }, [newWorkOrder.completedAt, newWorkOrder.startedAt]);

  useEffect(() => {
    if (!newWorkOrder.startedAt || actualDuration === '') return;
    const start = new Date(newWorkOrder.startedAt);
    const hrs = parseFloat(actualDuration);
    if (isNaN(start) || isNaN(hrs)) return;
    if (!actualDurationEditedRef.current && prevStartedAtRef.current === newWorkOrder.startedAt) return;
    const end = new Date(start.getTime() + hrs * 3600000);
    const iso = end.toISOString();
    if (newWorkOrder.completedAt !== iso) {
      setNewWorkOrder((prev) => ({ ...prev, completedAt: iso }));
    }
    prevStartedAtRef.current = newWorkOrder.startedAt;
  }, [actualDuration, newWorkOrder.startedAt]);

  useEffect(() => {
    if (newWorkOrder.statusId === 4) return;
    setErrors((prev) => {
      const { actualDuration, ...rest } = prev;
      return rest;
    });
  }, [newWorkOrder.statusId]);

  const fetchWorkOrderDetails = async (wo) => {
    isSavingRef.current = true;
    try {
      const parts = await getWorkOrderPartsByWorkOrderId(wo.id);
      const [labour, services, equipment] = await Promise.all([
        getBillOfLabourWosByWorkOrderId(newWorkOrder.id),
        getBillOfServicesWosByWorkOrderId(newWorkOrder.id),
        getBillOfEquipmentWosByWorkOrderId(newWorkOrder.id),
      ]);
      const partsWithSource = await Promise.all(
        parts.map(async (p) => {
          if (p.source === 'PURCHASE ORDER' && p.poLineId) {
            try {
              const line = await getPurchaseOrderLineById(p.poLineId);
              const po = await getPurchaseOrderById(line.purchaseOrderId);
              return { ...p, poNumber: po.poNumber };
            } catch (err) {
              console.error('Error loading purchase order for part', err);
            }
          }
          if (p.source === 'WAREHOUSE') {
            return { ...p, poNumber: null };
          }
          return p;
        })
      );
      setWorkOrderParts(partsWithSource);
      const equip = await getBillOfEquipmentWosByWorkOrderId(wo.id);
      setWorkOrderEquipment(equip);
      const lab = await getBillOfLabourWosByWorkOrderId(wo.id);
      setWorkOrderLabour(lab);
      const serv = await getBillOfServicesWosByWorkOrderId(wo.id);
      const servicesWithSource = await Promise.all(
        serv.map(async (s) => {
          if (s.source === 'PURCHASE ORDER' && s.poLineId) {
            try {
              const line = await getPurchaseOrderLineById(s.poLineId);
              const po = await getPurchaseOrderById(line.purchaseOrderId);
              return { ...s, poNumber: po.poNumber };
            } catch (err) {
              console.error('Error loading purchase order for part', err);
            }
          }
          return s;
        })
      );
      setWorkOrderServices(servicesWithSource);
      const costItems = await getCostItemsByWorkOrderId(wo.id);
      const normalizedCostItems = Array.isArray(costItems) ? costItems : [];
      setWorkOrderCostItems(normalizedCostItems);
      const primaryCost = normalizedCostItems[0] || null;
      setActualPartsCost(
        formatCostInputValue(getCostItemFieldValue(primaryCost, ['partsActualCost', 'parts_actual_cost'])),
      );
      setActualLabourCost(
        formatCostInputValue(getCostItemFieldValue(primaryCost, ['labourActualCost', 'labour_actual_cost'])),
      );
      setActualMiscCost(
        formatCostInputValue(getCostItemFieldValue(primaryCost, ['miscActualCost', 'misc_actual_cost'])),
      );
      setActualLabourHours(
        formatCostInputValue(
          getCostItemFieldValue(primaryCost, [
            'actualLabourHours',
            'actual_labour_hours',
            'labourActualHours',
            'labour_actual_hours',
          ]),
        ),
      );
      const forms = await getWorkOrderFormsByWorkOrderId(wo.id);
      setWorkOrderForms(forms);
    } catch (error) {
      console.error('Error loading parts for work order:', error);
      setWorkOrderParts([]);
      setWorkOrderEquipment([]);
      setWorkOrderLabour([]);
      setWorkOrderServices([]);
      setWorkOrderCostItems([]);
      setActualPartsCost('');
      setActualLabourCost('');
      setActualMiscCost('');
      setActualLabourHours('');
      setWorkOrderForms([]);
    }
  };

  const handleView = async (wo) => {
    setFormReadOnly(true);
    setModalLoading(true);
    try {
      const fresh = await getWorkOrderById(wo.id);
      const converted = {
        ...fresh,
        workorderFiles: Array.isArray(fresh.workorderFiles)
          ? fresh.workorderFiles.map((f) =>
            typeof f === 'string' ? { name: f, filename: f } : f,
          )
          : [],
        workOrderImage: Array.isArray(fresh.workOrderImage) ? fresh.workOrderImage : [],
        crew: normalizeIdArray(fresh.crew),
        completionImages: normalizeImageArray(fresh.completionImages),
        costResponsibilityId: fresh.costResponsibilityId ?? 1,
      };
      setNewWorkOrder({ ...converted, createEvent: false });
      originalWorkOrderRef.current = JSON.parse(JSON.stringify(converted));
      setActualDuration('');
      actualDurationEditedRef.current = false;
      prevStartedAtRef.current = null;
      await fetchWorkOrderDetails(fresh);
    } catch (err) {
      console.error('Error loading work order:', err);
      setNewWorkOrder({
        ...wo,
        createEvent: false,
        costResponsibilityId: wo.costResponsibilityId ?? 1,
      });
      setWorkOrderCostItems([]);
      setActualPartsCost('');
      setActualLabourCost('');
      setActualMiscCost('');
      setActualLabourHours('');
      setActualDuration('');
      actualDurationEditedRef.current = false;
      prevStartedAtRef.current = null;
    }
    setModalLoading(false);

    setShowView(true);
  };

  // Open modal to edit
  const handleEdit = async (wo) => {
    setFormReadOnly(false);
    setModalLoading(true);
    durationEditedRef.current = false;
    prevScheduledAtRef.current = null;
    actualDurationEditedRef.current = false;
    prevStartedAtRef.current = null;
    try {
      const fresh = await getWorkOrderById(wo.id);
      const converted = {
        ...fresh,
        workorderFiles: Array.isArray(fresh.workorderFiles)
          ? fresh.workorderFiles.map((f) =>
            typeof f === 'string' ? { name: f, filename: f } : f,
          )
          : [],
        workOrderImage: Array.isArray(fresh.workOrderImage) ? fresh.workOrderImage : [],
        crew: normalizeIdArray(fresh.crew),
        completionImages: normalizeImageArray(fresh.completionImages),
        costResponsibilityId: fresh.costResponsibilityId ?? 1,
      };
      setActualDuration('');
      setNewWorkOrder({ ...converted, createEvent: false });
      originalWorkOrderRef.current = JSON.parse(JSON.stringify(converted));
      if (fresh.workOrderScheduleId) {
        try {
          const sched = await getSchedule(fresh.workOrderScheduleId);
          const scheduleData = {
            ...sched,
            templateWorkOrderId: Number(sched.templateWorkOrderId),
          };
          setWorkOrderSchedule(scheduleData);
          originalScheduleRef.current = JSON.parse(
            JSON.stringify(scheduleData),
          );
        } catch (err) {
          console.error('Error loading schedule', err);
          setWorkOrderSchedule(null);
          originalScheduleRef.current = null;
        }
      } else {
        setWorkOrderSchedule(null);
        originalScheduleRef.current = null;
      }
      if (fresh.assetId) {
        setSelectedAssign({ value: fresh.assetId, label: `${fresh.asset?.assetNumber || ''} ${fresh.asset?.assetDescription || ''}`, type: 'Asset' });
        setAssignType('asset');
        try {
          const asset = await getAssetById(fresh.assetId);
          setSelectedAssetDetails(asset);
          setIsAdvancedAsset(asset?.PlanningType?.typeDescription?.toLowerCase() === 'advanced');
        } catch (err) {
          console.error('Error loading asset info', err);
          setSelectedAssetDetails(null);
          setIsAdvancedAsset(false);
        }
      } else if (fresh.areaId) {
        setSelectedAssign({ value: fresh.areaId, label: `${fresh.area?.name || ''}`, type: 'Location' });
        setAssignType('area');
        setIsAdvancedAsset(false);
      } else {
        setSelectedAssign(null);
        setAssignType('all');
        setIsAdvancedAsset(false);
      }
      setEditingId(fresh.id);
      await fetchWorkOrderDetails(fresh);
      const durationBase = Number(fresh.statusId) === 2 ? fresh.startedAt : fresh.scheduledAt;
      if (durationBase && fresh.dueDate) {
        const diffMs = new Date(fresh.dueDate) - new Date(durationBase);
        if (!isNaN(diffMs)) {
          const hrs = Math.max(0, diffMs / 3600000);
          setDuration(hrs.toFixed(2));
        } else {
          setDuration('');
        }
        prevScheduledAtRef.current = durationBase;
      } else {
        setDuration('');
      }
    } catch (err) {
      console.error('Error loading work order:', err);
      setNewWorkOrder({
        ...wo,
        createEvent: false,
        costResponsibilityId: wo.costResponsibilityId ?? 1,
      });
      setWorkOrderCostItems([]);
      setActualPartsCost('');
      setActualLabourCost('');
      setActualMiscCost('');
      setActualLabourHours('');
      setEditingId(wo.id);
      setActualDuration('');
      actualDurationEditedRef.current = false;
      prevStartedAtRef.current = null;
    }
    setModalLoading(false);

    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete work order?')) {
      await deleteWorkOrder(id);
      setWorkOrders((prev) => prev.filter((row) => row.id !== id));
    }
  };

  const handleCloneWorkOrder = async (wo) => {
    try {
      const data = { ...wo };
      delete data.id;
      const clean = {};
      for (const key in data) {
        const value = data[key];
        const isInvalidKey =
          key === 'workOrderNumber' ||
          key === 'workOrderScheduleId' ||
          value === null ||
          typeof value === 'object';

        if (!isInvalidKey) {
          clean[key] = value;
        }
      }
      const { partsCount, ...cleanWithoutPartsCount } = clean;
      const payload = {
        ...cleanWithoutPartsCount,
        workOrderScheduleId: wo.workOrderScheduleId,
        scheduledAt: wo.scheduledAt ? toUtcIsoWithTime(wo.scheduledAt) : null,
        dueDate: wo.dueDate ? toUtcIsoWithTime(wo.dueDate) : null,
        startedAt: wo.startedAt ? toUtcIsoWithTime(wo.startedAt) : null,
        completedAt: wo.completedAt ? toUtcIsoWithTime(wo.completedAt) : null,
        crew: normalizeIdArray(wo.crew),
        eventId: null,
        workorderFiles: wo.workorderFiles,
        workOrderImages: wo.workOrderImages,
        completionImages: normalizeImageArray(wo.completionImages),
        departmentId: wo.departmentId,
        organisationId: userProfile.org_id,
        createdAt: new Date().toISOString(),
        createdBy: userProfile.id,
      };

      const created = await createWorkOrder(payload);
      updateWorkOrders();
      if (viewMode === 'panel') {
        const fresh = await getWorkOrderById(created.id);
        setSelectedWorkOrder(fresh);
        await fetchWorkOrderDetails(fresh);
      }
    } catch (err) {
      console.error('Error cloning work order:', err);
    }
  };

  const handleFileDelete = async (filename, idx) => {
    if (!window.confirm('Delete this file?')) return;

    const currentFiles = Array.isArray(newWorkOrder.workorderFiles) ? newWorkOrder.workorderFiles : [];
    const fileEntry = currentFiles[idx];
    const { key: derivedKey, url: derivedUrl } = extractDeletionTargets(fileEntry, filename);
    const normalizedFilename = normalizeFileKey(filename);
    const deletionKey = derivedKey || normalizedFilename || filename;

    setNewWorkOrder((prev) => {
      const nextFiles = Array.isArray(prev.workorderFiles)
        ? prev.workorderFiles.filter((_, i) => i !== idx)
        : prev.workorderFiles;

      const nextState = {
        ...prev,
        workorderFiles: nextFiles,
      };

      const urlProps = [
        'workorderFileUrls',
        'workOrderFileUrls',
        'workorderFilesUrls',
        'workOrderFilesUrls',
      ];

      urlProps.forEach((prop) => {
        if (Array.isArray(prev[prop])) {
          const filtered = removeMatchingUrls(prev[prop], deletionKey ? normalizeFileKey(deletionKey) : '', derivedUrl);
          if (filtered !== prev[prop]) {
            nextState[prop] = filtered;
          }
        }
      });

      return nextState;
    });

    if (!deletionKey) return;

    try {
      await deleteUploadedFile(deletionKey, 'workorderfiles');
    } catch (err) {
      console.error('Error deleting file', err);
    }
  };


  const handleDurationBlur = () => {
    const num = parseFloat(duration);
    if (duration === '' || isNaN(num) || num < 0 || num > 9999) {
      setErrors((prev) => ({ ...prev, duration: 'Duration must be between 0 and 9999 hours' }));
      durationEditedRef.current = true;
      return;
    }
    setErrors((prev) => {
      const { duration, ...rest } = prev;
      return rest;
    });
    durationEditedRef.current = false;
  };

  const handleActualDurationBlur = () => {
    const num = parseFloat(actualDuration);
    if (actualDuration === '' || isNaN(num) || num < 0 || num > 9999) {
      setErrors((prev) => ({ ...prev, actualDuration: 'Actual duration must be between 0 and 9999 hours' }));
      actualDurationEditedRef.current = true;
      return;
    }
    setErrors((prev) => {
      const { actualDuration, ...rest } = prev;
      return rest;
    });
    actualDurationEditedRef.current = false;
  };

  const handleExportExcel = () => exportWorkOrdersExcel(filteredWorkOrders);
  const handleExportCSV = () => exportWorkOrdersCSV(filteredWorkOrders);
  const handleExportPdf = async (wo) => {
    const targetWorkOrder = wo || selectedWorkOrder;
    if (!targetWorkOrder) return;

    try {
      await exportWorkOrderPdf(targetWorkOrder);
    } catch (err) {
      console.error('Error exporting work order PDF', err);
    }
  };

  const handleAddToEvent = (wo) => {
    handleEdit(wo);
  };

  const clearForm = ({ preserveDetails = false } = {}) => {
    setNewWorkOrder({
      statutory: 0,
      workorderFiles: [],
      workOrderImage: [],
      completionImages: [],
      createEvent: false,
      costResponsibilityId: 1,
    });
    setEditingId(null);
    setSelectedAssetDetails(null);
    setErrors({});
    setDuration('');
    setActualDuration('');
    setActualPartsCost('');
    setActualLabourCost('');
    setActualMiscCost('');
    setActualLabourHours('');
    durationEditedRef.current = false;
    actualDurationEditedRef.current = false;
    prevScheduledAtRef.current = null;
    prevStartedAtRef.current = null;
    originalWorkOrderRef.current = null;
    setDeletedPartIds([]);
    setDeletedEquipmentIds([]);
    setDeletedLabourIds([]);
    setDeletedServiceIds([]);
    setSelectedAssign(null);
    setAssignType('all');
    setWorkOrderSchedule(null);
    originalScheduleRef.current = null;
    scheduleChangedRef.current = false;
    setAssetTaskOptions([]);
    if (!preserveDetails) {
      setWorkOrderParts([]);
      setWorkOrderEquipment([]);
      setWorkOrderLabour([]);
      setWorkOrderServices([]);
      setWorkOrderCostItems([]);
    }
  };

  const closeModal = (workOrderOverride = null) => {
    setShowModal(false);
    // document.body.classList.remove('modal-open');
    // const backdrop = document.querySelector('.modal-backdrop');
    // if (backdrop) backdrop.remove();
    const targetWorkOrder = workOrderOverride || selectedWorkOrder;
    const preserveDetails = viewMode === 'panel' && targetWorkOrder;
    clearForm({ preserveDetails });
    const shouldRefetchDetails =
      preserveDetails &&
      (!workOrderOverride || workOrderOverride.id === selectedWorkOrder?.id);
    if (shouldRefetchDetails) {
      fetchWorkOrderDetails(targetWorkOrder);
    }
  };
  const closeEquipmentModal = () => {
    setEquipmentErrors({});
    setEditingEquipmentIndex(null);
    setShowEquipmentModal(false);
  };
  const closeLabourModal = () => {
    setLabourErrors({});
    setShowLabourModal(false);
    setEditingLabourIndex(null);
  };
  const closeFormModal = () => {
    setShowFormModal(false);
    setFormTargetWorkOrder(null);
  };


  const openAddFormModal = async (workOrderOverride = null) => {
    try {
      const [assignments, forms] = await Promise.all([
        getFormAssignments(),
        getForms(),
      ]);
      const targetWo = workOrderOverride || newWorkOrder;
      const targetWorkOrderId = workOrderOverride?.id || targetWo?.id || editingId;
      if (!targetWorkOrderId) {
        console.warn('No work order selected for adding forms');
        return;
      }
      let assetClassId = null;
      let assetGroupId = null;
      if (targetWo?.assetId) {
        const asset = await getAssetById(targetWo.assetId);
        assetClassId = asset?.group?.classId;
        assetGroupId = asset?.groupId;
      }
      const parseArr = (val) => {
        if (!val) return [];
        let arr = [];
        if (Array.isArray(val)) arr = val;
        else if (typeof val === 'object') {
          try {
            arr = Array.isArray(val.ids) ? val.ids : Object.values(val);
          } catch {
            arr = [];
          }
        } else {
          arr = [val];
        }
        return arr.map((n) => parseInt(n, 10));
      };
      const filtered = assignments.filter((a) => {
        const ac = parseArr(a.assetClassIds);
        const ag = parseArr(a.assetGroupIds);
        const tm = parseArr(a.teamIds);
        const dp = parseArr(a.departmentIds);
        const allClass = ac.includes(-1);
        const allGroup = ag.includes(-1);
        const allTeam = tm.includes(-1);
        const allDept = dp.includes(-1);

        const classMatch = allClass || ac.includes(assetClassId);
        const groupMatch = allGroup || ag.includes(assetGroupId);
        const teamMatch = allTeam || tm.includes(targetWo?.teamsId);
        const deptMatch = allDept || dp.includes(targetWo?.departmentId);

        return classMatch || groupMatch || teamMatch || deptMatch;
      });
      const ids = [...new Set(filtered.map((f) => f.formId))];
      const existingFormIds = workOrderForms.map((wf) => wf.formId);
      const opts = forms
        .filter((f) => ids.includes(f.id) && !existingFormIds.includes(f.id))
        .map((f) => ({ value: f.id, label: f.name }));
      setFormOptions(opts);
      setSelectedFormId('');
      setFormTargetWorkOrder({
        id: targetWorkOrderId,
        assetId: targetWo?.assetId || null,
        areaId: targetWo?.areaId || 0,
        departmentId: targetWo?.departmentId || null,
        teamId: targetWo?.teamsId || null,
      });
      setShowFormModal(true);
    } catch (err) {
      console.error('Error loading forms for work order', err);
    }
  };

  const handleSaveFormSelection = async () => {
    if (!selectedFormId) return;
    const userId = userProfile?.id ?? null;
    const targetContext = formTargetWorkOrder || {
      id: editingId,
      assetId: newWorkOrder.assetId || null,
      areaId: newWorkOrder.areaId || 0,
      departmentId: newWorkOrder.departmentId || null,
      teamId: newWorkOrder.teamsId || null,
    };
    if (!targetContext?.id) {
      console.warn('No work order selected for saving forms');
      return;
    }
    try {
      const newWoForm = await createWorkOrderForm({
        formId: parseInt(selectedFormId, 10),
        workOrderId: targetContext.id,
        assetId: targetContext.assetId,
        locationId: targetContext.areaId,
        createdBy: userId,
      });
      const forms = await getWorkOrderFormsByWorkOrderId(targetContext.id);
      setWorkOrderForms(forms);

      // copy form items to form-data table and form actions to work-order-form-actions
      try {
        const form = await getFormById(parseInt(selectedFormId, 10));
        const items = form.tblFormItems || [];
        await Promise.all(
          items.map((item) =>
            createFormData({
              formItemId: item.id,
              formId: item.formId,
              workOrderFormId: newWoForm.id,
              description: item.description,
              width: item.width,
              images: item.images,
              heading: item.heading,
              formInputTypeId: item.formInputTypeId,
              instructions: item.instructions,
              includePhoto: item.includePhoto,
              disableComments: item.disableComments,
              raiseDefects: item.raiseDefects,
              required: item.required,
              data: item.data || null,
              sortOrder: item.sortOrder,
              assetId: targetContext.assetId,
              departmentId: targetContext.departmentId,
              teamId: targetContext.teamId,
              createdBy: userId,
            }),
          ),
        );
        const actions = form.tblFormActions || [];
        await Promise.all(
          actions.map((action) =>
            createWorkOrderFormAction({
              workOrderFormId: newWoForm.id,
              actionData: action.actionData,
              formActionId: action.formActionId,
              createdBy: userId,
            }),
          ),
        );
      } catch (copyErr) {
        console.error('Error copying form items or actions', copyErr);
      }

      setShowFormModal(false);
      setFormTargetWorkOrder(null);
    } catch (err) {
      console.error('Error saving work order form', err);
    }
  };

  const handleDeleteForm = async (id) => {
    if (!window.confirm('Delete form?')) return;
    try {
      const actions = await getWorkOrderFormActionsByWorkOrderFormId(id);
      if (actions.length) {
        await Promise.all(actions.map((a) => deleteWorkOrderFormAction(a.id)));
      }
      await deleteWorkOrderForm(id);
      setWorkOrderForms((prev) => prev.filter((f) => f.id !== id));
    } catch (err) {
      console.error('Error deleting work order form', err);
    }
  };

  const handleViewForm = (form) => {
    setViewWorkOrderFormId(form.id);
    setShowFormView(true);
  };

  const handlePrintForm = (pdfLink) => {
    if (!pdfLink) return;
    window.open(pdfLink, '_blank', 'noopener,noreferrer');
  };

  const performSave = async () => {
    const savedId = editingId;
    let createdId = null;
    try {
      const normalizedDuration = normalizeDurationValue(duration);
      const shouldNormalizeDuration = normalizedDuration !== duration;
      if (shouldNormalizeDuration) {
        setDuration(normalizedDuration);
      }
      const saveDurationBase = Number(newWorkOrder.statusId) === 2 ? newWorkOrder.startedAt : newWorkOrder.scheduledAt;
      let dueDateOverride = null;
      if (normalizedDuration !== '' && normalizedDuration !== null && normalizedDuration !== undefined && saveDurationBase) {
        const start = new Date(saveDurationBase);
        const hrs = parseFloat(normalizedDuration);
        if (!Number.isNaN(start.getTime()) && Number.isFinite(hrs)) {
          const end = new Date(start.getTime() + hrs * 3600000);
          dueDateOverride = end.toISOString();
        }
      }
      let updatedWorkOrder = {
        ...newWorkOrder,
        scheduledAt: normalizeDateValue(newWorkOrder.scheduledAt),
        dueDate: normalizeDateValue(dueDateOverride ?? newWorkOrder.dueDate),
      };
      const shouldCreateEvent = Boolean(
        updatedWorkOrder.createEvent &&
        isAdvancedAsset &&
        canCreateWorkOrderEvent,
      );
      if (updatedWorkOrder.taskId === '' || updatedWorkOrder.taskId === undefined) {
        updatedWorkOrder = { ...updatedWorkOrder, taskId: null, cycleGroupOrder: null };
      }
      let scheduleAction = null;
      let schedulePayload = null;
      if (scheduleChangedRef.current && workOrderSchedule) {
        if (!workOrderSchedule.active) {
          scheduleAction = 'delete';
          updatedWorkOrder = { ...updatedWorkOrder, workOrderScheduleId: null };
        } else {
          scheduleAction = 'update';
          schedulePayload = {
            templateWorkOrderId: parseInt(
              workOrderSchedule.templateWorkOrderId,
              10,
            ),
            recurrencePattern: workOrderSchedule.recurrencePattern,
            intervalValue: parseInt(workOrderSchedule.intervalValue, 10) || 0,
            daysOfWeek: workOrderSchedule.daysOfWeek || null,
            dayOfMonth:
              workOrderSchedule.dayOfMonth != null
                ? parseInt(workOrderSchedule.dayOfMonth, 10)
                : null,
            monthOfYear:
              workOrderSchedule.monthOfYear != null
                ? parseInt(workOrderSchedule.monthOfYear, 10)
                : null,
            startDate: workOrderSchedule.startDate
              ? new Date(workOrderSchedule.startDate).toISOString()
              : null,
            endDate: workOrderSchedule.endDate
              ? new Date(workOrderSchedule.endDate).toISOString()
              : null,
            occurrenceCount:
              workOrderSchedule.occurrenceCount != null
                ? parseInt(workOrderSchedule.occurrenceCount, 10)
                : null,
            noEndDate: !!workOrderSchedule.noEndDate,
            active: workOrderSchedule.active ? 1 : 0,
          };
        }
      }

      if (!autoGenWorkOrderNo && editingId) {
        const existing = await findWorkOrderByNumber((updatedWorkOrder.workOrderNumber || '').trim());
        if (existing && existing.id !== updatedWorkOrder.id) {
          setErrors({ workOrderNumber: 'Existing Work Order Number found.' });
          return;
        }
      }

      let departmentId = updatedWorkOrder.departmentId;
      if (updatedWorkOrder.assetId) {
        try {
          const asset = await getAssetById(updatedWorkOrder.assetId);
          departmentId = asset?.assetDepartmentId || departmentId;
        } catch (err) {
          console.error('Error loading asset department', err);
        }
      } else if (updatedWorkOrder.areaId) {
        try {
          const area = await getAreaById(updatedWorkOrder.areaId);
          departmentId = area?.departmentId || departmentId;
        } catch (err) {
          console.error('Error loading area department', err);
        }
      }

      const woData = { ...updatedWorkOrder };

      const cleanPayload = {};
      for (const key in woData) {
        const value = woData[key];
        const isTaskField = key === 'taskId' || key === 'cycleGroupOrder';
        if (key === 'createEvent') continue;
        if (!editingId && key === 'workOrderNumber') continue;
        if (
          key !== 'workOrderScheduleId' &&
          typeof value !== 'object' &&
          (value !== null || isTaskField)
        ) {
          cleanPayload[key] = value;
        }
      }

      const { partsCount, ...cleanPayloadWithoutPartsCount } = cleanPayload;

      const existingEventId = woData.eventId || null;

      const workOrderPayload = {
        ...cleanPayloadWithoutPartsCount,
        workOrderScheduleId: woData.workOrderScheduleId,
        ...(editingId ? { workOrderNumber: woData.workOrderNumber } : {}),
        scheduledAt: woData.scheduledAt ? toUtcIsoWithTime(woData.scheduledAt) : null,
        dueDate: woData.dueDate ? toUtcIsoWithTime(woData.dueDate) : null,
        startedAt: woData.startedAt ? toUtcIsoWithTime(woData.startedAt) : null,
        completedAt: woData.completedAt ? toUtcIsoWithTime(woData.completedAt) : null,
        taskCodeId: woData.taskCodeId,
        taskId: woData.taskId,
        cycleGroupOrder: woData.cycleGroupOrder ?? null,
        crew: normalizeIdArray(woData.crew),
        workorderFiles: woData.workorderFiles,
        workOrderImage: woData.workOrderImage,
        completionImages: normalizeImageArray(woData.completionImages),
        departmentId,
        eventId: existingEventId,
        organisationId: userProfile.org_id,
      };
      delete workOrderPayload.createEvent;

      let sameAsOriginal = false;
      let scheduleIdChanged = false;
      if (editingId && originalWorkOrderRef.current) {
        const orig = originalWorkOrderRef.current;
        const cleanOrig = {};
        for (const key in orig) {
          if (orig[key] !== null && typeof orig[key] !== 'object') {
            cleanOrig[key] = orig[key];
          }
        }
        const { partsCount: origPartsCount, ...cleanOrigWithoutPartsCount } = cleanOrig;
        const originalPayload = {
          ...cleanOrigWithoutPartsCount,
          scheduledAt: orig.scheduledAt ? toUtcIsoWithTime(orig.scheduledAt) : null,
          dueDate: orig.dueDate ? toUtcIsoWithTime(orig.dueDate) : null,
          startedAt: orig.startedAt ? toUtcIsoWithTime(orig.startedAt) : null,
          completedAt: orig.completedAt ? toUtcIsoWithTime(orig.completedAt) : null,
          crew: normalizeIdArray(orig.crew),
          workorderFiles: orig.workorderFiles,
          workOrderImage: orig.workOrderImage,
          completionImages: normalizeImageArray(orig.completionImages),
          departmentId: orig.departmentId,
          eventId: orig.eventId,
          organisationId: userProfile.org_id,
        };
        delete originalPayload.createEvent;
        scheduleIdChanged =
          workOrderPayload.workOrderScheduleId !==
          originalPayload.workOrderScheduleId;
        const currentCompare = { ...workOrderPayload };
        const originalCompare = { ...originalPayload };
        delete currentCompare.workOrderScheduleId;
        delete originalCompare.workOrderScheduleId;
        sameAsOriginal = deepEqual(currentCompare, originalCompare);
      }

      workOrderPayload.isModified = sameAsOriginal ? 0 : 1;

      let refreshId = savedId;

      if (editingId) {
        if (!sameAsOriginal || scheduleIdChanged) {
          delete workOrderPayload.workOrderNumber;
          await updateWorkOrder(editingId, {
            ...workOrderPayload,
            updatedAt: new Date().toISOString(),
            updatedBy: userProfile.id,
          });
        }

        const syncedParts = await saveWorkOrderParts(editingId, workOrderParts);
        setWorkOrderParts(syncedParts);

        const updatedEquip = workOrderEquipment.map((e) =>
          e.id
            ? update('bill-of-equipment-wos', e.id, e)
            : create('bill-of-equipment-wos', { ...e, workOrderId: editingId })
        );
        await Promise.all(updatedEquip);

        const updatedLab = workOrderLabour.map((l) =>
          l.id
            ? update('bill-of-labour-wo', l.id, l)
            : create('bill-of-labour-wo', { ...l, workOrderId: editingId })
        );
        await Promise.all(updatedLab);

        const updatedServ = workOrderServices.map((s) => {
          delete s.purchaseOrderNumber;
          delete s.poNumber;

          return s.id
            ? update('bill-of-services-wo', s.id, s)
            : create('bill-of-services-wo', { ...s, workOrderId: editingId });
        });

        await Promise.all(updatedServ);

        const hasActualCostInput = [actualPartsCost, actualLabourCost, actualMiscCost, actualLabourHours]
          .some((value) => value !== '' && value !== null && value !== undefined);
        const labourHoursValue = Number.isFinite(labourHoursTotal) ? labourHoursTotal : 0;
        if (primaryCostItem) {
          const updatedCostItem = await updateCostItem(primaryCostItem.id, {
            workOrderId: editingId,
            partsActualCost: parseCostInputValue(actualPartsCost),
            labourActualCost: parseCostInputValue(actualLabourCost),
            miscActualCost: parseCostInputValue(actualMiscCost),
            actualLabourHours: parseCostInputValue(actualLabourHours),
            labourHours: labourHoursValue,
            updatedAt: new Date().toISOString(),
            updatedBy: userProfile.id,
          });
          const updatedCostItems = [...workOrderCostItems];
          updatedCostItems[0] = updatedCostItem;
          setWorkOrderCostItems(updatedCostItems);
        } else if (hasActualCostInput || labourHoursValue !== 0) {
          const createdCostItem = await createCostItem({
            workOrderId: editingId,
            partsActualCost: parseCostInputValue(actualPartsCost),
            labourActualCost: parseCostInputValue(actualLabourCost),
            miscActualCost: parseCostInputValue(actualMiscCost),
            actualLabourHours: parseCostInputValue(actualLabourHours),
            labourHours: labourHoursValue,
            createdAt: new Date().toISOString(),
            createdBy: userProfile.id,
          });
          setWorkOrderCostItems([createdCostItem]);
        }
      } else {
        const payloadForCreate = {
          ...workOrderPayload,
          craetedAt: new Date().toISOString(),
          createdBy: userProfile.id,
        };

        if (
          Number(payloadForCreate.statusId) === 2 &&
          !payloadForCreate.scheduledAt &&
          payloadForCreate.startedAt
        ) {
          payloadForCreate.scheduledAt = payloadForCreate.startedAt;
        }

        let created;
        try {
          created = shouldCreateEvent
            ? await createWorkOrderAndEvent(payloadForCreate)
            : await createWorkOrder(payloadForCreate);
        } catch (err) {
          if (shouldCreateEvent) {
            const message =
              err?.response?.data?.message ||
              err?.message ||
              'Event already exists for this asset at that date and time';
            const statusId = Number(newWorkOrder.statusId);
            const eventFieldErrors = {};
            if (statusId === 2 || statusId === 3) {
              eventFieldErrors.startedAt = message;
              eventFieldErrors.dueDate = message;
            } else if (statusId === 4) {
              eventFieldErrors.startedAt = message;
              eventFieldErrors.completedAt = message;
            } else {
              eventFieldErrors.scheduledAt = message;
              eventFieldErrors.dueDate = message;
            }
            setCreateEventErrors(eventFieldErrors);
          }
          console.error('Error creating work order', err);
          return;
        }
        const newId = created.id;
        createdId = newId;
        refreshId = newId;
        setCreateEventErrors({});

        const newParts = workOrderParts
          .filter((p) => !p.id)
          .map((p) => ({
            ...p,
            workOrderId: newId,
            cost: parseFloat(p.cost || 0),
          }));
        await Promise.all(newParts.map((p) => create('work-order-parts', p)));

        await Promise.all(
          workOrderEquipment.map((e) =>
            create('bill-of-equipment-wos', { ...e, workOrderId: newId })
          )
        );
        await Promise.all(
          workOrderLabour.map((l) =>
            create('bill-of-labour-wo', { ...l, workOrderId: newId })
          )
        );

        const servicesSansPO = workOrderServices.map((s) => {
          const copy = { ...s };
          delete (copy).poNumber;
          return copy;
        });

        await Promise.all(
          servicesSansPO.map((s) =>
            create('bill-of-services-wo', { ...s, workOrderId: newId })
          )
        );

        const hasActualCostInput = [actualPartsCost, actualLabourCost, actualMiscCost, actualLabourHours]
          .some((value) => value !== '' && value !== null && value !== undefined);
        const labourHoursValue = Number.isFinite(labourHoursTotal) ? labourHoursTotal : 0;
        const shouldPersistCostItem = hasActualCostInput || labourHoursValue !== 0;
        const costItems = await getCostItemsByWorkOrderId(newId);
        const normalizedCostItems = Array.isArray(costItems) ? costItems : [];
        const createdPrimaryCostItem = normalizedCostItems[0] || null;
        if (createdPrimaryCostItem) {
          const updatedCostItem = await updateCostItem(createdPrimaryCostItem.id, {
            workOrderId: newId,
            partsActualCost: parseCostInputValue(actualPartsCost),
            labourActualCost: parseCostInputValue(actualLabourCost),
            miscActualCost: parseCostInputValue(actualMiscCost),
            actualLabourHours: parseCostInputValue(actualLabourHours),
            labourHours: labourHoursValue,
            updatedAt: new Date().toISOString(),
            updatedBy: userProfile.id,
          });
          const updatedCostItems = [...normalizedCostItems];
          updatedCostItems[0] = updatedCostItem;
          setWorkOrderCostItems(updatedCostItems);
        } else if (shouldPersistCostItem) {
          const createdCostItem = await createCostItem({
            workOrderId: newId,
            partsActualCost: parseCostInputValue(actualPartsCost),
            labourActualCost: parseCostInputValue(actualLabourCost),
            miscActualCost: parseCostInputValue(actualMiscCost),
            actualLabourHours: parseCostInputValue(actualLabourHours),
            labourHours: labourHoursValue,
            createdAt: new Date().toISOString(),
            createdBy: userProfile.id,
          });
          setWorkOrderCostItems([createdCostItem]);
        }
      }

      if (scheduleAction === 'delete') {
        try {
          await deleteSchedule(workOrderSchedule.id);
          setWorkOrderSchedule(null);
          setNewWorkOrder((prev) => ({ ...prev, workOrderScheduleId: null }));
          originalScheduleRef.current = null;
        } catch (err) {
          console.error('Error deleting schedule', err);
        }
      } else if (scheduleAction === 'update') {
        try {
          await updateSchedule(workOrderSchedule.id, schedulePayload);
          originalScheduleRef.current = JSON.parse(
            JSON.stringify(workOrderSchedule),
          );
        } catch (err) {
          console.error('Error updating schedule', err);
        }
      }

      if (deletedPartIds.length > 0) {
        await Promise.all(deletedPartIds.map((id) => deleteOne('work-order-parts', id)));
        setDeletedPartIds([]);
      }

      if (deletedEquipmentIds.length > 0) {
        await Promise.all(deletedEquipmentIds.map((id) => deleteOne('bill-of-equipment-wos', id)));
        setDeletedEquipmentIds([]);
      }
      if (deletedLabourIds.length > 0) {
        await Promise.all(deletedLabourIds.map((id) => deleteOne('bill-of-labour-wo', id)));
        setDeletedLabourIds([]);
      }
      if (deletedServiceIds.length > 0) {
        await Promise.all(deletedServiceIds.map((id) => deleteOne('bill-of-services-wo', id)));
        setDeletedServiceIds([]);
      }

      let refreshedWorkOrder = null;
      if (refreshId) {
        try {
          refreshedWorkOrder = await getWorkOrderById(refreshId);
        } catch (err) {
          console.error('Error refreshing work order after save:', err);
        }
      }

      closeModal(refreshedWorkOrder);
      setShowModal(false);
      setEditingId(null);
      // setSearchTerm('');
      updateWorkOrders();
      setCalendarRefresh((c) => c + 1);
      setNewWorkOrder({
        statutory: 0,
        workorderFiles: [],
        workOrderImage: [],
        completionImages: [],
        createEvent: false,
        costResponsibilityId: 1,
      });
      setWorkOrderParts([]);
      setWorkOrderEquipment([]);
      setWorkOrderLabour([]);
      setWorkOrderServices([]);

      if (refreshedWorkOrder) {
        if (viewMode === 'panel') {
          setSelectedWorkOrder(refreshedWorkOrder);
        } else {
          setSelectedWorkOrder((prev) =>
            prev && prev.id === refreshedWorkOrder.id ? refreshedWorkOrder : prev
          );
        }
        if (showView) {
          const converted = {
            ...refreshedWorkOrder,
            workorderFiles: Array.isArray(refreshedWorkOrder.workorderFiles)
              ? refreshedWorkOrder.workorderFiles.map((f) =>
                typeof f === 'string' ? { name: f, filename: f } : f,
              )
              : [],
            workOrderImage: Array.isArray(refreshedWorkOrder.workOrderImage)
              ? refreshedWorkOrder.workOrderImage
              : [],
            crew: normalizeIdArray(refreshedWorkOrder.crew),
            completionImages: normalizeImageArray(refreshedWorkOrder.completionImages),
            costResponsibilityId: refreshedWorkOrder.costResponsibilityId ?? 1,
          };
          setNewWorkOrder({ ...converted, createEvent: false });
        }
      }

      if (!editingId && createdId) {
        await handleEdit({ id: createdId });
      }
      scheduleChangedRef.current = false;
    } catch (error) {
      console.error('Error saving work order:', error);
    } finally {
      isSavingRef.current = false;
    }
  };

  const handleSave = async () => {
    const validationErrors = {
      ...validateForm(workOrderValidationFields, newWorkOrder),
      ...getWorkOrderStatusValidationErrors({
        newWorkOrder,
        editingId,
        duration,
        actualDuration,
      }),
    };

    const assetStartDate = selectedAssetDetails?.startDate || newWorkOrder?.asset?.startDate;
    const assetEndDate = selectedAssetDetails?.endDate || newWorkOrder?.asset?.endDate;
    const parseDate = (value) => {
      if (!value) return null;
      const date = new Date(value);
      return isNaN(date.getTime()) ? null : date;
    };

    const assetStart = parseDate(assetStartDate);
    const assetEnd = parseDate(assetEndDate);

    if (newWorkOrder.assetId && (assetStart || assetEnd)) {
      const scheduledAt = parseDate(newWorkOrder.scheduledAt);
      const startedAt = parseDate(newWorkOrder.startedAt);

      if (scheduledAt) {
        if (assetStart && scheduledAt < assetStart) {
          validationErrors.scheduledAt = 'Scheduled start cannot be before asset start date';
        } else if (assetEnd && scheduledAt > assetEnd) {
          validationErrors.scheduledAt = 'Scheduled start cannot be after asset end date';
        }
      }

      if (startedAt) {
        if (assetStart && startedAt < assetStart) {
          validationErrors.startedAt = 'Start time cannot be before asset start date';
        } else if (assetEnd && startedAt > assetEnd) {
          validationErrors.startedAt = 'Start time cannot be after asset end date';
        }
      }
    }

    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    const shouldCreateEvent =
      !editingId && newWorkOrder.createEvent && isAdvancedAsset && canCreateWorkOrderEvent;
    if (shouldCreateEvent) {
      const eventValid = validateCreateEvent();
      if (!eventValid) {
        return;
      }
    } else {
      setCreateEventErrors({});
    }

    scheduleChangedRef.current =
      !!(
        workOrderSchedule &&
        originalScheduleRef.current &&
        !deepEqual(workOrderSchedule, originalScheduleRef.current)
      );

    if (scheduleChangedRef.current && workOrderSchedule) {
      try {
        const resp = await getPaginatedWorkOrders({
          page: 1,
          pageSize: 1000,
          workOrderScheduleId: workOrderSchedule.id,
        });
        const items = resp.data || resp;
        const modified = items.filter(
          (wo) =>
            wo.id !== Number(workOrderSchedule.templateWorkOrderId) &&
            wo.isModified === 1,
        );
        if (modified.length > 0) {
          setShowScheduleWarning(true);
          return;
        }
      } catch (err) {
        console.error('Error checking schedule work orders', err);
      }
    }

    await performSave();
  };

  const handleSaveLabour = () => {
    const baseValid = validateLabour();
    const extraErrors = {};

    const qtyValue = Number(labourForm.qty);
    if (!Number.isFinite(qtyValue) || qtyValue < 0 || !Number.isInteger(qtyValue)) {
      extraErrors.qty = 'Quantity must be a positive whole number';
    }

    const hoursValue = Number(labourForm.labourHours);
    if (!Number.isFinite(hoursValue) || hoursValue < 0) {
      extraErrors.labourHours = 'Hours must be a positive number';
    }

    const rateValue = Number(labourForm.labourRate);
    if (!Number.isFinite(rateValue) || rateValue < 0) {
      extraErrors.labourRate = 'Labour rate must be a positive number';
    }

    if (Object.keys(extraErrors).length > 0) {
      setLabourErrors((prev) => ({ ...prev, ...extraErrors }));
    }

    if (!baseValid || Object.keys(extraErrors).length > 0) {
      return;
    }

    const formattedLabour = {
      ...labourForm,
      tradeId: labourForm.tradeId === '' ? null : Number(labourForm.tradeId),
      defaultExpenseCode:
        labourForm.defaultExpenseCode === '' ? null : Number(labourForm.defaultExpenseCode),
      qty: qtyValue,
      labourHours: hoursValue,
      labourRate: rateValue,
    };

    if (editingLabourIndex !== null) {
      setWorkOrderLabour((prev) => prev.map((l, i) => (i === editingLabourIndex ? formattedLabour : l)));
    } else {
      setWorkOrderLabour((prev) => [...prev, formattedLabour]);
    }

    setEditingLabourIndex(null);
    setLabourErrors({});
    setShowLabourModal(false);
  };

  const handleSaveEquipment = () => {
    const errors = {};

    const nameValue = (equipmentForm.name || '').trim();
    if (!nameValue) {
      errors.name = 'Name is required';
    }

    const quantityValue = Number(equipmentForm.quantity);
    if (!Number.isFinite(quantityValue) || quantityValue < 0 || !Number.isInteger(quantityValue)) {
      errors.quantity = 'Quantity must be zero or a whole number';
    }

    const costValue =
      equipmentForm.cost === '' || equipmentForm.cost === null || equipmentForm.cost === undefined
        ? 0
        : Number(equipmentForm.cost);
    if (!Number.isFinite(costValue) || costValue < 0) {
      errors.cost = 'Cost must be zero or a positive number';
    }

    if (Object.keys(errors).length > 0) {
      setEquipmentErrors(errors);
      return;
    }

    const formattedEquipment = {
      ...equipmentForm,
      name: nameValue,
      quantity: quantityValue,
      cost: costValue,
    };

    if (editingEquipmentIndex !== null) {
      setWorkOrderEquipment((prev) => prev.map((e, i) => (i === editingEquipmentIndex ? formattedEquipment : e)));
    } else {
      setWorkOrderEquipment((prev) => [...prev, formattedEquipment]);
    }

    setEditingEquipmentIndex(null);
    setEquipmentErrors({});
    setShowEquipmentModal(false);
  };

  return (
    <>
      {headerSearchContainer &&
        createPortal(
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />,
          headerSearchContainer,
        )}
      <div className="card h-100 p-0 rounded-0">
        <div className="card-header d-flex justify-content-between align-items-center border-0 pb-16">
          <div className="d-flex flex-wrap align-items-center gap-2">
            {renderFilterControls()}
          </div>
          <div className="d-flex align-items-center gap-2 ms-auto me-8">
            <div className="dropdown me-2">
              <button
                className="btn btn-outline-secondary text-sm px-12 py-12 radius-4 dropdown-toggle"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                Export Data
              </button>
              <ul className="dropdown-menu p-12 border bg-base shadow">
                <li>
                  <button className="dropdown-item" onClick={handleExportExcel}>
                    Excel
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={handleExportCSV}>
                    CSV
                  </button>
                </li>
              </ul>
            </div>
            {canCreateWorkOrder && (
              <div className="btn-group">
                <button
                  className="btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2"
                  onClick={async () => {
                    setModalLoading(true);
                    try {
                      const initNewWorkOrder = getInitialWorkOrder();

                      setNewWorkOrder(initNewWorkOrder);
                      setIsAdvancedAsset(false);
                      setEditingId(null);
                      setFormReadOnly(false);
                      setWorkOrderParts([]);
                      setWorkOrderCostItems([]);
                      durationEditedRef.current = false;
                      actualDurationEditedRef.current = false;
                      setActualDuration('');
                      setActualPartsCost('');
                      setActualLabourCost('');
                      setActualMiscCost('');
                      setActualLabourHours('');
                      prevStartedAtRef.current = null;
                      setShowModal(true);
                    } finally {
                      setModalLoading(false);
                    }
                  }}
                >
                  <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" /> Add Work Order
                </button>
                <button
                  type="button"
                  className="btn btn-primary text-sm btn-sm dropdown-toggle dropdown-toggle-split"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <span className="visually-hidden">Toggle Dropdown</span>
                </button>
                <ul className="dropdown-menu dropdown-menu-end">
                  <li className="my-6">
                    <button
                      className="dropdown-item py-8 d-flex align-items-center gap-2"
                      onClick={(e) => {
                        const toggleBtn = e.currentTarget
                          .closest('.btn-group')
                          ?.querySelector('.dropdown-toggle');
                        if (toggleBtn) {
                          Dropdown.getOrCreateInstance(toggleBtn).hide();
                        }
                        openAiModal();
                      }}
                    >
                      <Icon icon="mdi:stars-outline" className="icon text-xl line-height-1" />
                      Add With AI
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="card-body p-0">
          {viewMode === 'list' && (
            <div className="card h-100 p-0 rounded-0">
              <div className="card-body p-0">
                <div
                  className="paginated-table-container scroll"
                  ref={listContainerRef}
                  style={{ maxHeight: 'calc(100vh - 210px)' }}
                >
                  <WorkOrderDataTable
                    workOrders={filteredWorkOrders}
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onAddToEvent={handleAddToEvent}
                    onCloneWorkOrder={handleCloneWorkOrder}
                    onExportPdf={handleExportPdf}
                    canUpdateWorkOrder={canUpdateWorkOrder}
                    loading={woLoading}
                    sortField={sortField}
                    sortOrder={sortOrder}
                    onSort={handleSortChange}
                  />
                </div>
              </div>
            </div>
          )}
          {viewMode === 'calendar' && (
            <WorkOrderCalendarLayer
              userProfile={userProfile}
              handleView={handleView}
              handleEdit={handleEdit}
              setShowModal={setShowModal}
              setNewWorkOrder={setNewWorkOrder}
              setEditingId={setEditingId}
              setFormReadOnly={setFormReadOnly}
              refreshTrigger={calendarRefresh}
              filters={appliedFilters}
            />
          )}
          {viewMode === 'panel' && (
            <WorkOrderPanelView
              workOrders={filteredWorkOrders}
              setWorkOrders={setWorkOrders}
              selectedWorkOrder={selectedWorkOrder}
              setSelectedWorkOrder={setSelectedWorkOrder}
              listContainerRef={listContainerRef}
              woLoading={woLoading}
              handleEdit={handleEdit}
              parts={workOrderParts}
              labour={workOrderLabour}
              services={workOrderServices}
              equipment={workOrderEquipment}
              forms={workOrderForms}
              actualCosts={actualCostTotals}
              onViewForm={handleViewForm}
              onAddForm={openAddFormModal}
              crew={crew}
              notFound={notFound}
              onExportPdf={handleExportPdf}
              onCloneWorkOrder={handleCloneWorkOrder}
              onDeleteWorkOrder={handleDelete}
              updateWorkOrders={updateWorkOrders}
              assignmentFilter={panelAssignmentFilter}
              onAssignmentFilterChange={handlePanelAssignmentChange}
              panelSortOption={panelSortOption}
              onPanelSortOptionChange={handlePanelSortChange}
            />
          )}
        </div>
        <WorkOrderEditModal
          showModal={showModal}
          editingId={editingId}
          newWorkOrder={newWorkOrder}
          modalLoading={modalLoading}
          generalTabHasError={generalTabHasError}
          showCreateEventToggle={showCreateEventToggle}
          onClose={closeModal}
          onCreateEventToggle={handleCreateEventToggle}
          canUpdateWorkOrder={canUpdateWorkOrder}
          onJobReadyStatusToggle={(value) => setNewWorkOrder((prev) => ({ ...prev, jobReadyStatus: value }))}
          workOrderGeneralFields={workOrderGeneralFields}
          renderGeneralFields={renderGeneralFields}
          duration={duration}
          onDurationChange={handleDurationChange}
          combinedErrors={combinedErrors}
          onDurationBlur={handleDurationBlur}
          actualDuration={actualDuration}
          onActualDurationChange={handleActualDurationChange}
          onActualDurationBlur={handleActualDurationBlur}
          workOrderCompleteFields={workOrderCompleteFields}
          renderCompleteFields={renderCompleteFields}
          renderProgressFields={renderProgressFields}
          partsCostTotal={partsCostTotal}
          actualPartsCost={actualPartsCost}
          onActualPartsCostChange={setActualPartsCost}
          labourCostTotal={labourCostTotal}
          actualLabourCost={actualLabourCost}
          onActualLabourCostChange={setActualLabourCost}
          servicesCostTotal={servicesCostTotal}
          actualMiscCost={actualMiscCost}
          onActualMiscCostChange={setActualMiscCost}
          labourHoursTotal={labourHoursTotal}
          actualLabourHours={actualLabourHours}
          onActualLabourHoursChange={setActualLabourHours}
          workOrderSchedule={workOrderSchedule}
          scheduleFields={scheduleFields}
          renderScheduleField={renderScheduleField}
          workOrderParts={workOrderParts}
          setWorkOrderParts={setWorkOrderParts}
          deletedPartIds={deletedPartIds}
          setDeletedPartIds={setDeletedPartIds}
          userProfile={userProfile}
          expenseOptions={expenseOptions}
          defaultPartsExpenseCode={defaultPartsExpenseCode}
          workOrderLabour={workOrderLabour}
          setWorkOrderLabour={setWorkOrderLabour}
          deletedLabourIds={deletedLabourIds}
          setDeletedLabourIds={setDeletedLabourIds}
          tradeOptions={tradeOptions}
          defaultLabourExpenseCode={defaultLabourExpenseCode}
          setLabourForm={setLabourForm}
          setEditingLabourIndex={setEditingLabourIndex}
          setLabourErrors={setLabourErrors}
          setShowLabourModal={setShowLabourModal}
          isWorkOrderLocked={isWorkOrderLocked}
          workOrderServices={workOrderServices}
          setWorkOrderServices={setWorkOrderServices}
          setDeletedServiceIds={setDeletedServiceIds}
          defaultServiceExpenseCode={defaultServiceExpenseCode}
          serviceOptions={serviceOptions}
          workOrderEquipment={workOrderEquipment}
          setWorkOrderEquipment={setWorkOrderEquipment}
          deletedEquipmentIds={deletedEquipmentIds}
          setDeletedEquipmentIds={setDeletedEquipmentIds}
          setEditingEquipmentIndex={setEditingEquipmentIndex}
          setEquipmentForm={setEquipmentForm}
          setEquipmentErrors={setEquipmentErrors}
          setShowEquipmentModal={setShowEquipmentModal}
          workOrderForms={workOrderForms}
          onViewForm={handleViewForm}
          onPrintForm={handlePrintForm}
          onDeleteForm={handleDeleteForm}
          onOpenAddFormModal={openAddFormModal}
          workOrderDocumentFields={workOrderDocumentFields}
          renderDocumentFields={renderDocumentFields}
          onFileDelete={handleFileDelete}
          handleSave={handleSave}
          showEquipmentModal={showEquipmentModal}
          editingEquipmentIndex={editingEquipmentIndex}
          equipmentForm={equipmentForm}
          equipmentErrors={equipmentErrors}
          clearEquipmentFieldError={clearEquipmentFieldError}
          onCloseEquipmentModal={closeEquipmentModal}
          onSaveEquipment={handleSaveEquipment}
          showLabourModal={showLabourModal}
          editingLabourIndex={editingLabourIndex}
          labourForm={labourForm}
          labourErrors={labourErrors}
          clearLabourFieldError={clearLabourFieldError}
          onCloseLabourModal={closeLabourModal}
          onSaveLabour={handleSaveLabour}
          showFormModal={showFormModal}
          selectedFormId={selectedFormId}
          formOptions={formOptions}
          onSelectedFormChange={setSelectedFormId}
          onCloseFormModal={closeFormModal}
          onSaveFormSelection={handleSaveFormSelection}
        />

        {showView && (
          <WorkOrderViewModal
            workOrder={newWorkOrder}
            parts={workOrderParts}
            equipment={workOrderEquipment}
            labour={workOrderLabour}
            services={workOrderServices}
            forms={workOrderForms}
            onViewForm={handleViewForm}
            onExportPdf={handleExportPdf}
            crew={crew}
            onClose={() => setShowView(false)}
            onEdit={(wo) => { setShowView(false); handleEdit(wo); }}
          />
        )}

        {showAiModal && (
          <div className="modal modal-backdrop show d-block" tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5
                    className="modal-title">
                    <Icon className="text-xxl me-12 text-primary" style={{ position: "relative", top: "-4px" }} icon="mdi:stars-outline" />
                    Create a Work Order
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeAiModal}
                    disabled={aiLoading}
                  />
                </div>
                <div className="modal-body">
                  <div className="text-sm text-secondary">
                    <p>
                      Use this to create a new work order using plain text. To ensure the work
                      order can be reviewed and raised correctly, please include the following
                      details where applicable.
                    </p>

                    <ul
                      style={{
                        listStyleType: 'disc',
                        listStylePosition: 'outside'
                      }}
                      className="ps-24"
                    >
                      <li>Asset number or asset name</li>
                      <li>A clear description of the work to be carried out</li>
                      <li>Whether this is a breakdown or reactive job</li>
                      <li>Requested or scheduled start and end dates</li>
                      <li>Any compliance, safety, or regulatory requirements</li>
                      <li>Priority, access constraints, or special instructions if relevant</li>
                    </ul>
                    <br />
                    <p>
                      Providing complete and clear information helps ensure the work order is created
                      accurately, prioritised correctly, and actioned without delay.
                    </p>
                  </div>

                  {aiError && (
                    <div className="alert alert-danger" role="alert">
                      {aiError}
                    </div>
                  )}
                  <textarea
                    className="form-control"
                    rows={6}
                    value={aiPromptText}
                    onChange={(e) => setAiPromptText(e.target.value)}
                    disabled={aiLoading}
                    placeholder="Describe the work order you want to create"
                  />
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={closeAiModal}
                    disabled={aiLoading}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleAiCreateWorkOrder}
                    disabled={aiLoading || !aiPromptText.trim()}
                  >
                    {aiLoading ? 'Creating...' : 'Add With AI'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showScheduleWarning && (
          <div className="modal modal-backdrop show d-block" tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Update Schedule?</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowScheduleWarning(false)}
                  />
                </div>
                <div className="modal-body">
                  Some work orders in the schedule have been modified, do you want to update the schedule? This will delete any changes to future work orders.
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowScheduleWarning(false)}
                  >
                    No
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={async () => {
                      setShowScheduleWarning(false);
                      await performSave();
                    }}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}



        {showFormView && (
          <FormViewModal
            workOrderFormId={viewWorkOrderFormId}
            onClose={() => {
              setShowFormView(false);
              setViewWorkOrderFormId(null);
            }}
          />
        )}

        {showImageModal && (
          <div className="modal modal-backdrop show d-block" onClick={() => setShowImageModal(false)} tabIndex="-1">
            <div className="modal-dialog" style={{ maxWidth: '75%' }}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <img src={imageModalSrc} alt="view" style={{ width: '100%' }} />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default WorkOrderListLayer;
